import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../api";
import { useAuth } from "../../AuthContext";
import MgmtLayout from "../MgmtLayout";
import ThumbUploader from "../ThumbUploader";
import ExpensesTab from "./ExpensesTab";
import { Plus, Trash2, X, Upload, FileText, Loader2, Check as CheckIcon, Crown } from "lucide-react";

const UNITS = ["g", "kg", "ml", "l", "piece"];
const inputCls = "bg-cream border border-ink/15 rounded-sm px-3 py-2 focus:outline-none focus:border-matcha-dark transition-colors text-ink text-sm";
const fmt = (v) => `RM ${Number(v || 0).toFixed(2)}`;

export default function MgmtCatalog() {
  const [tab, setTab] = useState("products");
  return (
    <MgmtLayout title="Products & Inventory" subtitle="— Catalog">
      <div className="flex items-center gap-6 border-b border-ink/15 mb-8">
        {[
          { k: "products", label: "Products" },
          { k: "ingredients", label: "Ingredients" },
          { k: "purchases", label: "Purchases" },
          { k: "batches", label: "Batches / SKU" },
          { k: "opex", label: "Operating Costs" },
          { k: "expenses", label: "Expenses" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`catalog-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`pb-3 -mb-px overline transition-colors ${
              tab === t.k ? "text-matcha-dark border-b-2 border-matcha-dark" : "text-ink-soft hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "products" && <ProductsTab />}
      {tab === "ingredients" && <IngredientsTab />}
      {tab === "purchases" && <PurchasesTab />}
      {tab === "batches" && <BatchesTab />}
      {tab === "opex" && <OpexTab />}
      {tab === "expenses" && <ExpensesTab />}
    </MgmtLayout>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [ings, setIngs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [buildingRecipes, setBuildingRecipes] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { src, name }
  const [expanded, setExpanded] = useState({}); // baseName -> bool
  const [selected, setSelected] = useState(new Set()); // Set<product.id>
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const [p, i] = await Promise.all([
      api.get("/mgmt/products").then((r) => r.data),
      api.get("/mgmt/ingredients").then((r) => r.data),
    ]);
    setProducts(p); setIngs(i);
    setSelected(new Set());
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete product?")) return;
    await api.delete(`/mgmt/products/${id}`); load();
  };

  // Bulk-delete every ticked product in one backend call. Confirmation shows
  // the exact count so a mis-tap on the header checkbox cannot wipe the whole
  // catalog silently. ALL-CAPS admin-curated products are protected on the
  // backend and skipped from the first call — we surface a second confirm
  // with the protected names so admin can opt in explicitly (force=true).
  const deleteSelected = async () => {
    if (selected.size === 0) return;
    const doomed = products.filter((p) => selected.has(p.id));
    const names = doomed.slice(0, 5).map((p) => p.name).join(", ");
    if (!window.confirm(
      `Delete ${doomed.length} product${doomed.length === 1 ? "" : "s"}?\n\n` +
      `${names}${doomed.length > 5 ? `, +${doomed.length - 5} more` : ""}\n\n` +
      `Their recipes and thumbnails go with them. This cannot be undone.`
    )) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/mgmt/products/bulk-delete", { ids: Array.from(selected) });
      const protectedIds = data.protected || [];
      // Second confirm — the shop owner's real menu items were held back.
      if (protectedIds.length > 0) {
        const protectedProds = doomed.filter((p) => protectedIds.includes(p.id));
        const protectedNames = protectedProds.slice(0, 5).map((p) => p.name).join(", ");
        const alsoWipe = window.confirm(
          `Deleted ${data.deleted} product${data.deleted === 1 ? "" : "s"}.\n\n` +
          `${protectedIds.length} ALL-CAPS admin-keyed product${protectedIds.length === 1 ? "" : "s"} were protected:\n` +
          `${protectedNames}${protectedProds.length > 5 ? `, +${protectedProds.length - 5} more` : ""}\n\n` +
          `Delete these too? This looks like your real menu — click Cancel to keep them.`
        );
        if (alsoWipe) {
          const { data: forced } = await api.post("/mgmt/products/bulk-delete", {
            ids: protectedIds,
            force: true,
          });
          window.alert(`Force-deleted ${forced.deleted} admin-keyed product${forced.deleted === 1 ? "" : "s"}.`);
        }
      } else {
        window.alert(`Deleted ${data.deleted} product${data.deleted === 1 ? "" : "s"}.`);
      }
      await load();
    } catch (e) {
      window.alert(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const missingRecipeCount = products.filter((p) => !(p.recipe && p.recipe.length)).length;

  // Inline price save — patches the single product and updates local state
  // so the Price column stays live without a full refetch. Called from the
  // <PriceCell> below on blur / Enter.
  const savePrice = async (pid, price) => {
    const { data } = await api.patch(`/mgmt/products/${pid}`, { selling_price: Number(price) });
    setProducts((s) => s.map((p) => (p.id === pid ? { ...p, selling_price: data.selling_price } : p)));
  };

  // Group products by their "base name" — the drink name with any trailing
  // cup-size suffix (12oz / 16 oz / 12-oz) stripped. This gives the admin
  // one collapsible row per drink like "Matcha Latte - Coldwhisk", with the
  // 12oz + 16oz sizes tucked inside so each recipe stays edited separately.
  const groups = useMemo(() => {
    const stripSize = (n) => (n || "")
      .replace(/\s*\(?\s*\d+\s*[- ]?\s*oz\s*\)?\s*$/i, "")
      .trim() || (n || "").trim();
    const parseSize = (n) => {
      const m = (n || "").match(/(\d+)\s*[- ]?\s*oz/i);
      return m ? `${m[1]}oz` : "";
    };
    const map = new Map();
    for (const p of products) {
      const base = stripSize(p.name);
      const size = parseSize(p.name);
      const key = base.toLowerCase();
      if (!map.has(key)) map.set(key, { key, base_name: base, variants: [] });
      map.get(key).variants.push({ ...p, size });
    }
    // Sort variants inside each group by size ascending (12 → 16 → …)
    const oz = (v) => parseInt(v.size, 10) || 0;
    const list = Array.from(map.values());
    list.forEach((g) => g.variants.sort((a, b) => oz(a) - oz(b)));
    // Sort groups alphabetically by base name so the table is stable.
    list.sort((a, b) => a.base_name.localeCompare(b.base_name));
    return list;
  }, [products]);

  const allIds = useMemo(() => products.map((p) => p.id), [products]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleOne = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleGroup = (g) => setSelected((s) => {
    const next = new Set(s);
    const ids = g.variants.map((v) => v.id);
    const alreadyAll = ids.every((id) => next.has(id));
    if (alreadyAll) { ids.forEach((id) => next.delete(id)); }
    else { ids.forEach((id) => next.add(id)); }
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-ink-soft text-sm">
            {groups.length} drinks · {products.length} size variants
            {missingRecipeCount > 0 && (
              <span className="ml-2 text-amber-700">· {missingRecipeCount} without a recipe</span>
            )}
          </p>
          {selected.size > 0 && (
            <button
              type="button"
              data-testid="products-bulk-delete"
              disabled={bulkDeleting}
              onClick={deleteSelected}
              className="inline-flex items-center gap-2 rounded-full border border-red-600 bg-red-600 text-bone px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-red-700 hover:border-red-700 transition-colors disabled:opacity-50"
            >
              {bulkDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              {bulkDeleting ? "Deleting…" : `Delete selected (${selected.size})`}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="build-recipes-btn"
            onClick={() => setBuildingRecipes(true)}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2 border-2 border-matcha-dark text-matcha-dark text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark hover:text-bone transition-colors"
          >
            <Upload size={14} /> Scan Recipes
          </button>
          <button data-testid="add-product-btn" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 rounded-full px-5 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors">
            <Plus size={14} /> Add Product
          </button>
        </div>
      </div>
      {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mb-4">{err}</p>}
      <div className="bg-bone border border-ink/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sage/40">
            <tr className="text-left text-ink-soft">
              <th className="px-4 py-3 overline w-8">
                <input
                  type="checkbox"
                  data-testid="products-select-all"
                  className="w-4 h-4 accent-matcha-dark"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  aria-label="Select all products"
                />
              </th>
              <th className="px-4 py-3 overline w-8"></th>
              <th className="px-4 py-3 overline w-16"></th>
              <th className="px-4 py-3 overline">Drink · Size</th>
              <th className="px-4 py-3 overline">Category</th>
              <th className="px-4 py-3 overline">Price</th>
              <th className="px-4 py-3 overline">Recipe</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              // Singleton groups (only one size ever scanned) render as one
              // normal row — no need to expand something with a single child.
              if (g.variants.length === 1) {
                const p = g.variants[0];
                const isSel = selected.has(p.id);
                return (
                  <tr key={p.id} className={`border-t border-ink/5 ${isSel ? "bg-red-50/40" : ""}`} data-testid={`product-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        data-testid={`product-select-${p.id}`}
                        className="w-4 h-4 accent-matcha-dark"
                        checked={isSel}
                        onChange={() => toggleOne(p.id)}
                      />
                    </td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3">
                      {p.thumbnail_b64 ? (
                        <button type="button" data-testid={`product-thumb-${p.id}`}
                          onClick={() => setLightbox({ src: p.thumbnail_b64, name: p.name })}
                          className="block w-12 h-12 rounded-sm overflow-hidden border border-ink/10 hover:border-matcha-dark transition-colors"
                          title="Click to enlarge">
                          <img src={p.thumbnail_b64} alt={p.name} className="w-full h-full object-cover" />
                        </button>
                      ) : (
                        <div className="w-12 h-12 rounded-sm bg-sage/25 border border-dashed border-ink/15 flex items-center justify-center">
                          <FileText size={14} className="text-ink-soft/60" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        {p.admin_curated && (
                          <span
                            data-testid={`product-admin-crown-${p.id}`}
                            title="Admin-curated: ALL-CAPS naming marks this as your own menu record. Protected from bulk-delete and preferred by fuzzy match."
                            className="text-matcha-dark shrink-0"
                          >
                            <Crown size={14} />
                          </span>
                        )}
                        <span>{p.name}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-soft">{p.category || "—"}</td>
                    <td className="px-4 py-3"><PriceCell product={p} onSave={savePrice} /></td>
                    <td className={`px-4 py-3 ${p.recipe?.length ? "text-ink-soft" : "text-amber-700"}`}>
                      {p.recipe?.length ? `${p.recipe.length} ingredient(s)` : "— none —"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`product-edit-${p.id}`} onClick={() => setEditing(p)} className="text-xs uppercase tracking-[0.18em] text-matcha-dark hover:underline mr-3">Edit</button>
                      <button data-testid={`product-del-${p.id}`} onClick={() => del(p.id)} className="text-ink-soft hover:text-red-600"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                );
              }
              // Group with >1 size — one collapsible parent row + N variants.
              const isOpen = !!expanded[g.key];
              const firstThumb = g.variants.find((v) => v.thumbnail_b64)?.thumbnail_b64 || "";
              const withRecipe = g.variants.filter((v) => v.recipe && v.recipe.length).length;
              const groupAllSelected = g.variants.every((v) => selected.has(v.id));
              const groupSomeSelected = !groupAllSelected && g.variants.some((v) => selected.has(v.id));
              const priceRange = (() => {
                const prices = g.variants.map((v) => Number(v.selling_price || 0)).filter((n) => n > 0);
                if (!prices.length) return fmt(0);
                const min = Math.min(...prices), max = Math.max(...prices);
                return min === max ? fmt(min) : `${fmt(min)} – ${fmt(max)}`;
              })();
              return (
                <React.Fragment key={g.key}>
                  <tr
                    className={`border-t border-ink/5 cursor-pointer ${isOpen ? "bg-sage/10" : groupAllSelected ? "bg-red-50/40" : "hover:bg-ink/[0.02]"}`}
                    data-testid={`product-group-${g.key}`}
                    onClick={() => setExpanded((e) => ({ ...e, [g.key]: !e[g.key] }))}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        data-testid={`product-group-select-${g.key}`}
                        className="w-4 h-4 accent-matcha-dark"
                        checked={groupAllSelected}
                        ref={(el) => { if (el) el.indeterminate = groupSomeSelected; }}
                        onChange={() => toggleGroup(g)}
                      />
                    </td>
                    <td className="px-4 py-3 text-matcha-dark">{isOpen ? "▾" : "▸"}</td>
                    <td className="px-4 py-3">
                      {firstThumb ? (
                        <img src={firstThumb} alt={g.base_name} className="w-12 h-12 rounded-sm object-cover border border-ink/10" />
                      ) : (
                        <div className="w-12 h-12 rounded-sm bg-sage/25 border border-dashed border-ink/15 flex items-center justify-center">
                          <FileText size={14} className="text-ink-soft/60" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-ink font-medium inline-flex items-center gap-1.5">
                        {g.variants.some((v) => v.admin_curated) && (
                          <span
                            data-testid={`product-group-admin-crown-${g.key}`}
                            title="Admin-curated: one or more sizes here use ALL-CAPS naming. Protected from bulk-delete and preferred by fuzzy match."
                            className="text-matcha-dark shrink-0"
                          >
                            <Crown size={14} />
                          </span>
                        )}
                        {g.base_name}
                      </span>
                      <span className="ml-2 text-[11px] uppercase tracking-[0.18em] text-ink-soft">
                        {g.variants.map((v) => v.size || "—").join(" · ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-soft">{g.variants[0].category || "—"}</td>
                    <td className="px-4 py-3">{priceRange}</td>
                    <td className={`px-4 py-3 ${withRecipe === g.variants.length ? "text-ink-soft" : "text-amber-700"}`}>
                      {withRecipe}/{g.variants.length} sizes wired
                    </td>
                    <td className="px-4 py-3 text-right text-[11px] uppercase tracking-[0.18em] text-ink-soft">
                      {isOpen ? "hide" : "show"}
                    </td>
                  </tr>
                  {isOpen && g.variants.map((p) => {
                    const isSel = selected.has(p.id);
                    return (
                      <tr key={p.id} className={`border-t border-ink/5 ${isSel ? "bg-red-50/40" : "bg-bone/60"}`} data-testid={`product-row-${p.id}`}>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            data-testid={`product-select-${p.id}`}
                            className="w-4 h-4 accent-matcha-dark"
                            checked={isSel}
                            onChange={() => toggleOne(p.id)}
                          />
                        </td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3">
                          {p.thumbnail_b64 ? (
                            <button type="button" data-testid={`product-thumb-${p.id}`}
                              onClick={(e) => { e.stopPropagation(); setLightbox({ src: p.thumbnail_b64, name: p.name }); }}
                              className="block w-12 h-12 rounded-sm overflow-hidden border border-ink/10 hover:border-matcha-dark transition-colors"
                              title="Click to enlarge">
                              <img src={p.thumbnail_b64} alt={p.name} className="w-full h-full object-cover" />
                            </button>
                          ) : (
                            <div className="w-12 h-12 rounded-sm bg-sage/25 border border-dashed border-ink/15 flex items-center justify-center">
                              <FileText size={14} className="text-ink-soft/60" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 pl-8 text-ink-soft">
                          ↳ <span className="text-ink">{p.size || "—"}</span>
                        </td>
                        <td className="px-4 py-3 text-ink-soft">{p.category || "—"}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <PriceCell product={p} onSave={savePrice} />
                        </td>
                        <td className={`px-4 py-3 ${p.recipe?.length ? "text-ink-soft" : "text-amber-700"}`}>
                          {p.recipe?.length ? `${p.recipe.length} ingredient(s)` : "— none —"}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button data-testid={`product-edit-${p.id}`} onClick={(e) => { e.stopPropagation(); setEditing(p); }} className="text-xs uppercase tracking-[0.18em] text-matcha-dark hover:underline mr-3">Edit</button>
                          <button data-testid={`product-del-${p.id}`} onClick={(e) => { e.stopPropagation(); del(p.id); }} className="text-ink-soft hover:text-red-600"><Trash2 size={16} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <ChannelAddonsPanel />

      {(adding || editing) && (
        <ProductModal
          product={editing}
          products={products}
          ingredients={ings}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); load(); }}
        />
      )}
      {buildingRecipes && (
        <RecipeBuilderModal
          onClose={() => setBuildingRecipes(false)}
          onSaved={() => { setBuildingRecipes(false); load(); }}
        />
      )}
      {lightbox && (
        <ThumbnailLightbox
          src={lightbox.src}
          name={lightbox.name}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

// Full-screen preview for a product's recipe card thumbnail. Click backdrop
// or hit Esc to close.
// Inline price editor for the Products table. Shows the current RM figure
// in tabular-numerics; click to reveal a plain number input; blur or Enter
// commits, Escape reverts. Errors bounce the value back to what the server
// held so the table never displays a phantom edit.
function PriceCell({ product, onSave }) {
  const initial = product?.selling_price ?? 0;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(initial));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(String(product?.selling_price ?? 0)); }, [product?.selling_price]);
  const commit = async () => {
    const next = Number(value);
    if (Number.isNaN(next) || next < 0) {
      setValue(String(initial));
      setEditing(false);
      return;
    }
    if (next === Number(initial)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(product.id, next);
      setEditing(false);
    } catch (e) {
      window.alert(formatErr(e?.response?.data?.detail) || e.message);
      setValue(String(initial));
    } finally {
      setSaving(false);
    }
  };
  if (editing) {
    return (
      <input
        data-testid={`product-price-input-${product.id}`}
        type="number"
        step="0.01"
        min="0"
        autoFocus
        disabled={saving}
        className={inputCls + " w-24 text-right tabular-nums"}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === "Escape") { setValue(String(initial)); setEditing(false); }
        }}
      />
    );
  }
  return (
    <button
      type="button"
      data-testid={`product-price-${product.id}`}
      onClick={() => setEditing(true)}
      title="Click to edit price"
      className="tabular-nums text-ink hover:text-matcha-dark hover:underline decoration-dotted underline-offset-4 transition-colors"
    >
      {fmt(product?.selling_price ?? 0)}
    </button>
  );
}

// Purchases-tab P&L tag. Three canonical categories match the backend
// enum: COGS (default; not double-counted since live COGS reads recipes),
// Packaging (cups / straws / napkins), Others (cleaning agents,
// miscellaneous spend). Selection PATCHes on change and the parent
// optimistically updates rows so the pill reflects the choice instantly.
function PurchaseCategoryCell({ purchase, onChange }) {
  const current = purchase?.category || "COGS";
  const tone =
    current === "COGS" ? "text-ink-soft border-ink/15 bg-bone" :
    current === "Packaging" ? "text-amber-800 border-amber-300 bg-amber-50" :
    "text-matcha-dark border-matcha-dark/30 bg-sage/25";
  return (
    <select
      data-testid={`purchase-category-${purchase.id}`}
      value={current}
      onChange={(e) => onChange(purchase, e.target.value)}
      title="How the P&L should treat this purchase"
      className={`text-[11px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${tone} focus:outline-none focus:ring-1 focus:ring-matcha-dark cursor-pointer`}
    >
      <option value="COGS">COGS</option>
      <option value="Packaging">Packaging</option>
      <option value="Others">Others</option>
    </select>
  );
}

function ThumbnailLightbox({ src, name, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 bg-ink/80 flex items-center justify-center p-6 z-[60]"
      onClick={onClose}
      data-testid="product-thumb-lightbox"
    >
      <div className="max-w-4xl w-full max-h-[92vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img
          src={src}
          alt={name}
          className="max-w-full max-h-[80vh] rounded-sm shadow-2xl object-contain bg-bone"
        />
        <div className="flex items-center gap-3 mt-4">
          <p className="text-bone text-sm">{name}</p>
          <button
            type="button"
            data-testid="product-thumb-lightbox-close"
            onClick={onClose}
            className="text-[11px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border border-bone/40 text-bone hover:bg-bone hover:text-ink transition-colors"
          >
            Close (Esc)
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductModal({ product, products = [], ingredients, onClose, onSaved }) {
  const isNew = !product;
  const [form, setForm] = useState({
    name: product?.name || "",
    category: product?.category || "",
    selling_price: product?.selling_price ?? "",
    image_url: product?.image_url || "",
    active: product?.active ?? true,
    recipe: product?.recipe || [],
    // Cakes/tiramisu are baked as a WHOLE batch and sold by the slice.
    // Enter the whole recipe once, set "Yields: 10 pieces" and the system
    // shows both Batch COGS and Cost per Piece; live COGS on P&L divides
    // by the same yield. Default 1 = per-cup drinks unchanged.
    batch_yield: product?.batch_yield ?? 1,
  });
  const [err, setErr] = useState("");
  const addLine = () => setForm((s) => ({ ...s, recipe: [...s.recipe, { ingredient_id: "", qty: 0 }] }));
  const updLine = (i, k, v) => setForm((s) => ({ ...s, recipe: s.recipe.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const delLine = (i) => setForm((s) => ({ ...s, recipe: s.recipe.filter((_, idx) => idx !== i) }));

  // Only offer products that HAVE a recipe as a source. Also filter out the
  // product currently being edited so admins don't copy a recipe onto itself
  // (a no-op that just looks like a bug).
  const recipeSourceProducts = (products || [])
    .filter((p) => Array.isArray(p.recipe) && p.recipe.length > 0)
    .filter((p) => !product || p.id !== product.id)
    .sort((a, b) => a.name.localeCompare(b.name));

  const copyRecipeFrom = (sourceId) => {
    if (!sourceId) return;
    const src = recipeSourceProducts.find((p) => p.id === sourceId);
    if (!src) return;
    if (form.recipe.length > 0 && !window.confirm(
      `Replace the current ${form.recipe.length} ingredient line${form.recipe.length === 1 ? "" : "s"} with the recipe from "${src.name}"?`
    )) return;
    setForm((s) => ({
      ...s,
      // Deep-copy the recipe rows so future edits don't leak into the source
      // product's in-memory copy (they share the same reference otherwise).
      recipe: src.recipe.map((r) => ({ ingredient_id: r.ingredient_id, qty: r.qty })),
      // Carry the batch yield across too — copying a whole-cake recipe
      // (yield 10) onto a per-slice product needs the same yield to keep
      // the cost-per-piece math accurate.
      batch_yield: src.batch_yield ?? s.batch_yield,
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = {
        name: form.name,
        category: form.category,
        selling_price: Number(form.selling_price),
        image_url: form.image_url,
        active: form.active,
        recipe: form.recipe.filter((r) => r.ingredient_id && Number(r.qty) > 0)
                            .map((r) => ({ ingredient_id: r.ingredient_id, qty: Number(r.qty) })),
        batch_yield: Math.max(1, parseInt(form.batch_yield, 10) || 1),
      };
      if (isNew) await api.post("/mgmt/products", payload);
      else await api.patch(`/mgmt/products/${product.id}`, payload);
      onSaved();
    } catch (e2) { setErr(formatErr(e2?.response?.data?.detail) || e2.message); }
  };

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="product-modal">
      <form onSubmit={save} className="bg-bone w-full max-w-2xl rounded-sm p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>
            {isNew ? "New Product" : "Edit Product"}
          </h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><label className="overline text-ink-soft block mb-2">Name</label>
            <input data-testid="product-form-name" required className={inputCls + " w-full"} value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
          </div>
          <div><label className="overline text-ink-soft block mb-2">Category</label>
            <input
              className={inputCls + " w-full"}
              value={form.category}
              onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))}
              list="product-category-options"
              data-testid="product-form-category"
              placeholder="e.g. Drink - Matcha"
            />
            {/* Suggested taxonomy — free-text so admins can invent new
                buckets, but the datalist gets them to the canonical names
                (used by the Drinks / Desserts BI tabs) in one keystroke. */}
            <datalist id="product-category-options">
              <option value="Drink - Matcha" />
              <option value="Drink - Coffee" />
              <option value="Desserts" />
              <option value="Desserts - Combo" />
              <option value="Pastry" />
              <option value="Ice-cream" />
              <option value="Retail" />
              <option value="Merchandise" />
            </datalist>
          </div>
          <div><label className="overline text-ink-soft block mb-2">Selling price (RM)</label>
            <input data-testid="product-form-price" required type="number" step="0.01" className={inputCls + " w-full"} value={form.selling_price} onChange={(e) => setForm((s) => ({ ...s, selling_price: e.target.value }))} />
          </div>
          <div className="md:col-span-2"><label className="overline text-ink-soft block mb-2">Image URL (optional)</label>
            <input className={inputCls + " w-full"} value={form.image_url} onChange={(e) => setForm((s) => ({ ...s, image_url: e.target.value }))} />
          </div>
        </div>

        <div className="flex items-end justify-between gap-4 mt-8 mb-3">
          <p className="overline text-matcha-dark">— Recipe</p>
          <div className="flex items-center gap-4 flex-wrap justify-end">
            {/* Copy recipe from another product — pulls in every ingredient
                line + the source's batch_yield. Only source products with
                a non-empty recipe show up. The current product is filtered
                out so admins can't accidentally overwrite it with itself. */}
            {recipeSourceProducts.length > 0 && (
              <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-ink-soft">
                <span>Copy from</span>
                <select
                  data-testid="product-form-copy-recipe"
                  value=""
                  onChange={(e) => { copyRecipeFrom(e.target.value); e.target.value = ""; }}
                  className={inputCls + " min-w-[14rem]"}
                  title="Copy this product's ingredient list into the current recipe"
                >
                  <option value="">— Pick a product —</option>
                  {recipeSourceProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {p.recipe.length} item{p.recipe.length === 1 ? "" : "s"}
                      {p.batch_yield > 1 ? ` · yields ${p.batch_yield}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {/* Batch yield — for cakes/tiramisu the recipe below is the WHOLE
                bake; setting yield > 1 auto-divides ingredient qty & cost by
                the yield so live P&L and per-slice pricing stay accurate. */}
            <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-ink-soft">
              <span>Yields</span>
              <input
                data-testid="product-form-batch-yield"
                type="number"
                min="1"
                step="1"
                className={inputCls + " w-20 text-right tabular-nums"}
                value={form.batch_yield}
                onChange={(e) => setForm((s) => ({ ...s, batch_yield: e.target.value }))}
                title="Pieces produced per batch (10 slices per cheesecake)"
              />
              <span className="text-ink">pcs / batch</span>
            </label>
          </div>
        </div>
        <div className="space-y-2">
          {form.recipe.map((r, i) => {
            const ing = ingredients.find((x) => x.id === r.ingredient_id);
            // Live cost preview per row — pack-normalised, so this stays
            // accurate whether the ingredient is priced per gram (matcha
            // 1kg pack ÷ 1000) or naturally per-piece (cups, straws).
            const pack = Number(ing?.serves || 1) || 1;
            const perUnit = ing ? (Number(ing.cost_per_unit || 0) / pack) : 0;
            const rowCost = perUnit * Number(r.qty || 0);
            const measure = ing ? (["kg","g"].includes(ing.unit) ? "g" : ["l","litre","ml"].includes(ing.unit) ? "ml" : "piece") : "";
            return (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-6">
                  <select className={inputCls + " w-full"} value={r.ingredient_id} onChange={(e) => updLine(i, "ingredient_id", e.target.value)}>
                    <option value="">— Ingredient —</option>
                    {ingredients.map((ig) => <option key={ig.id} value={ig.id}>{ig.name} ({ig.unit})</option>)}
                  </select>
                </div>
                <div className="col-span-3">
                  <input type="number" step="0.001" placeholder={`Qty (${measure || ing?.unit || "unit"})`} className={inputCls + " w-full"} value={r.qty} onChange={(e) => updLine(i, "qty", e.target.value)} />
                </div>
                <div className="col-span-2 text-right pb-2.5 font-mono text-xs text-matcha-dark tabular-nums" data-testid={`recipe-line-cost-${i}`}
                     title={ing ? `${Number(r.qty || 0)} × RM ${perUnit.toFixed(4)}/${measure}` : "Pick an ingredient"}>
                  RM {rowCost.toFixed(4)}
                </div>
                <button type="button" onClick={() => delLine(i)} className="col-span-1 text-ink-soft hover:text-red-600 pb-2.5"><Trash2 size={16} /></button>
              </div>
            );
          })}
        </div>
        <button type="button" onClick={addLine} className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-matcha-dark hover:text-ink">
          <Plus size={14} /> Add ingredient
        </button>

        {form.recipe.length > 0 && (() => {
          // Total COGS for the recipe as entered (raw batch total). Matches
          // the backend `_product_cogs` formula exactly, so the number the
          // admin sees here is what flows into the P&L.
          const batchTotal = form.recipe.reduce((acc, r) => {
            const ing = ingredients.find((x) => x.id === r.ingredient_id);
            if (!ing) return acc;
            const pack = Number(ing.serves || 1) || 1;
            return acc + (Number(ing.cost_per_unit || 0) / pack) * Number(r.qty || 0);
          }, 0);
          const yieldN = Math.max(1, parseInt(form.batch_yield, 10) || 1);
          const perPiece = batchTotal / yieldN;
          const price = Number(form.selling_price || 0);
          const margin = price > 0 ? ((price - perPiece) / price) * 100 : 0;
          const isBatch = yieldN > 1;
          return (
            <div className="mt-4 border-t border-ink/10 pt-4 flex flex-wrap items-baseline gap-x-8 gap-y-2" data-testid="recipe-cost-summary">
              {isBatch && (
                <div>
                  <p className="overline text-ink-soft">Batch COGS</p>
                  <p className="font-mono text-lg text-ink-soft tabular-nums" data-testid="recipe-batch-cogs">RM {batchTotal.toFixed(4)}</p>
                </div>
              )}
              <div>
                <p className="overline text-ink-soft">{isBatch ? `Cost / piece (÷ ${yieldN})` : "Recipe COGS"}</p>
                <p className="font-mono text-lg text-matcha-dark tabular-nums" data-testid="recipe-cogs-total">RM {perPiece.toFixed(4)}</p>
              </div>
              {price > 0 && (
                <>
                  <div>
                    <p className="overline text-ink-soft">Gross margin</p>
                    <p className={`font-mono text-lg tabular-nums ${margin >= 60 ? "text-matcha-dark" : margin >= 40 ? "text-amber-800" : "text-red-700"}`}>
                      {margin.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="overline text-ink-soft">{isBatch ? "Profit / piece" : "Profit per cup"}</p>
                    <p className="font-mono text-lg text-ink tabular-nums">RM {(price - perPiece).toFixed(2)}</p>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mt-6">{err}</p>}
        <div className="flex justify-end gap-3 mt-8">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">Cancel</button>
          <button data-testid="product-save" className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors">Save</button>
        </div>
      </form>
    </div>
  );
}

const INGREDIENT_KINDS = [
  { k: "all", label: "All", colorCls: "" },
  { k: "physical", label: "Physical Items", colorCls: "text-ink bg-ink/8" },
  { k: "component", label: "Daily Components", colorCls: "text-matcha-dark bg-sage/40" },
  { k: "ingredient", label: "Daily Ingredients", colorCls: "text-amber-900 bg-amber-100/70" },
  { k: "matcha", label: "Matcha & Hojicha Powder", colorCls: "text-emerald-900 bg-emerald-100/70" },
  { k: "cups", label: "Cups", colorCls: "text-sky-900 bg-sky-100/70" },
  // "Piece" — items counted individually with a per-piece price (eggs,
  // cake boxes, cutlery, cartons). Unit auto-defaults to `piece` in the
  // Add Ingredient modal so cost/unit = price per one piece.
  { k: "piece", label: "Piece", colorCls: "text-rose-900 bg-rose-100/70" },
  // "Packaging" is the pickable tag that surfaces an ingredient in the
  // Channel Add-ons picker on Operating Costs. Any SKU flipped here
  // starts contributing per-cup COGS on the P&L for whichever channels
  // it's assigned to (Grab / Walk-in / …).
  { k: "packaging", label: "Packaging", colorCls: "text-amber-900 bg-amber-100/70" },
];
const kindMeta = (k) => INGREDIENT_KINDS.find((x) => x.k === k) || INGREDIENT_KINDS[1];

function IngredientsTab() {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [purchasingFor, setPurchasingFor] = useState(null);
  const [adjustingFor, setAdjustingFor] = useState(null);
  const [usingFor, setUsingFor] = useState(null);
  const [kindFilter, setKindFilter] = useState("all");
  const load = async () => {
    const { data } = await api.get("/mgmt/ingredients");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete ingredient?")) return;
    await api.delete(`/mgmt/ingredients/${id}`); load();
  };

  const setKind = async (r, newKind) => {
    await api.patch(`/mgmt/ingredients/${r.id}`, { kind: newKind });
    load();
  };

  // Inline unit change from the Ingredients table — one PATCH to
  // /mgmt/ingredients/{id} so admin can flip Fresh Milk 1L from "pack" to
  // "ml" without opening a full edit modal.
  const setUnit = async (r, newUnit) => {
    if (!newUnit || newUnit === r.unit) return;
    await api.patch(`/mgmt/ingredients/${r.id}`, { unit: newUnit });
    load();
  };

  const counts = useMemo(() => {
    const c = { physical: 0, component: 0, ingredient: 0, matcha: 0, cups: 0, piece: 0, packaging: 0 };
    rows.forEach((r) => { if (c[r.kind] !== undefined) c[r.kind] += 1; });
    return c;
  }, [rows]);
  const visible = kindFilter === "all" ? rows : rows.filter((r) => r.kind === kindFilter);

  const setStock = async (r, newStock) => {
    const v = Number(newStock);
    if (!Number.isFinite(v) || v < 0) return;
    if (v === Number(r.stock_qty)) return;
    await api.patch(`/mgmt/ingredients/${r.id}`, { stock_qty: v });
    load();
  };

  // Inline cost/unit edit. Weighted-average is normally updated by
  // purchases, but the admin sometimes needs to override manually — e.g.
  // when a recipe's cost is way off, or when the ingredient was seeded
  // with a placeholder RM 0. One PATCH, then reload so downstream COGS
  // in the P&L reflects the new figure immediately.
  const setCost = async (r, newCost) => {
    const v = Number(newCost);
    if (!Number.isFinite(v) || v < 0) return;
    if (v === Number(r.cost_per_unit)) return;
    await api.patch(`/mgmt/ingredients/${r.id}`, { cost_per_unit: v });
    load();
  };

  // Inline "# of serves" edit. Tells the P&L / cost-per-serve column how
  // many drink servings one purchase-unit yields (e.g. 1kg matcha bag →
  // 500 lattes). Zero or blank falls back to 1 so cost-per-serve = cost
  // per unit for naturally per-piece items (cups, straws).
  const setServes = async (r, newServes) => {
    const raw = String(newServes || "").trim();
    const v = raw === "" ? 1 : Number(raw);
    if (!Number.isFinite(v) || v < 0) return;
    if (v === Number(r.serves ?? 1)) return;
    await api.patch(`/mgmt/ingredients/${r.id}`, { serves: v });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="text-ink-soft text-sm">{visible.length} of {rows.length} ingredients · click <span className="text-matcha-dark">Purchases</span> to record new stock-in</p>
        <button data-testid="add-ingredient-btn" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 rounded-full px-5 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors">
          <Plus size={14} /> Add Ingredient
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-5" data-testid="ingredient-kind-filter">
        {INGREDIENT_KINDS.map((t) => {
          const n = t.k === "all" ? rows.length : counts[t.k];
          return (
            <button
              key={t.k}
              data-testid={`kind-tab-${t.k}`}
              onClick={() => setKindFilter(t.k)}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border transition-colors ${
                kindFilter === t.k
                  ? "bg-ink text-bone border-ink"
                  : "bg-bone text-ink-soft border-ink/15 hover:border-ink"
              }`}
            >
              {t.label} <span className="ml-1 text-[10px] opacity-70">{n}</span>
            </button>
          );
        })}
      </div>
      <div className="bg-bone border border-ink/10 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-sage/40">
            <tr className="text-left text-ink-soft">
              <th className="px-3 py-3 overline w-14">Photo</th>
              <th className="px-4 py-3 overline">Name</th>
              <th className="px-4 py-3 overline">Kind</th>
              <th className="px-4 py-3 overline">Code</th>
              <th className="px-4 py-3 overline">SKU</th>
              <th className="px-4 py-3 overline">Unit</th>
              <th className="px-4 py-3 overline">Cost/pack</th>
              <th className="px-4 py-3 overline" title="Usable qty inside one purchase unit (e.g. 850 for an 850g can, 1000 for 1kg bag, 100 for a 100-cup pack)">Pack size</th>
              <th className="px-4 py-3 overline">Cost/unit</th>
              <th className="px-4 py-3 overline">Stock</th>
              <th className="px-4 py-3 overline">Low threshold</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const low = r.stock_qty <= r.low_stock_threshold && r.low_stock_threshold > 0;
              const meta = kindMeta(r.kind);
              return (
                <tr key={r.id} className="border-t border-ink/5" data-testid={`ingredient-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <ThumbUploader
                      ingredient={r}
                      onUpdated={(updated) => {
                        setRows((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
                      }}
                    />
                  </td>
                  <td className="px-4 py-3">{r.name}</td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`ingredient-kind-${r.id}`}
                      value={r.kind || "physical"}
                      onChange={(e) => setKind(r, e.target.value)}
                      className={`text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-sm border border-transparent hover:border-ink/15 ${meta.colorCls}`}
                    >
                      {INGREDIENT_KINDS.filter((x) => x.k !== "all").map((x) => (
                        <option key={x.k} value={x.k}>{x.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-ink" data-testid={`ingredient-code-${r.id}`}>{r.code || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-ink-soft" data-testid={`ingredient-sku-${r.id}`}>{r.sku || "—"}</td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`ingredient-unit-${r.id}`}
                      value={r.unit || "ml"}
                      onChange={(e) => setUnit(r, e.target.value)}
                      className="text-[11px] uppercase tracking-[0.14em] px-2 py-1 rounded-sm border border-transparent hover:border-ink/15 bg-transparent text-ink"
                    >
                      {["ml", "g", "piece", "pack", "kg", "l", "litre", "can", "bottle", "box"].map((u) => (
                        <option key={u} value={u}>{u}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1">
                      <span className="text-[10px] text-ink-soft">RM</span>
                      <input
                        data-testid={`ingredient-cost-${r.id}`}
                        type="number"
                        step="0.0001"
                        min="0"
                        defaultValue={Number(r.cost_per_unit || 0).toFixed(4)}
                        key={`${r.id}-${r.cost_per_unit}`}
                        onBlur={(e) => setCost(r, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                        className="w-24 px-2 py-0.5 text-right font-mono text-sm bg-transparent border-b border-ink/10 focus:border-matcha-dark focus:outline-none text-ink"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <input
                      data-testid={`ingredient-serves-${r.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      defaultValue={r.serves ?? 1}
                      key={`${r.id}-serves-${r.serves ?? 1}`}
                      onBlur={(e) => setServes(r, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      title={`How much usable ${r.unit || "measure"} is inside one purchase unit (e.g. 850g in an 850g can). Recipes multiply against Cost/measure.`}
                      className="w-20 px-2 py-0.5 text-right font-mono text-sm bg-transparent border-b border-ink/10 focus:border-matcha-dark focus:outline-none text-ink"
                    />
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-ink-soft">
                    {(() => {
                      const s = Number(r.serves || 1);
                      const per = s > 0 ? Number(r.cost_per_unit || 0) / s : 0;
                      // Pick the "measure" label from the ingredient's unit —
                      // powders default to g, liquids to ml, pieces stay as
                      // is. Purely a display hint; the math is unitless.
                      const measure = ["kg","g"].includes(r.unit) ? "g"
                                    : ["l","litre","ml"].includes(r.unit) ? "ml"
                                    : "piece";
                      return `RM ${per.toFixed(4)} / ${measure}`;
                    })()}
                  </td>
                  <td className={`px-4 py-3 ${low ? "text-red-700" : "text-matcha-dark"}`}>
                    <div className="inline-flex items-center gap-1">
                      <input
                        data-testid={`ingredient-stock-${r.id}`}
                        type="number"
                        step="0.001"
                        min="0"
                        defaultValue={r.stock_qty}
                        onBlur={(e) => setStock(r, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                        className={`w-20 px-2 py-0.5 text-right font-mono text-sm bg-transparent border-b border-ink/10 focus:border-matcha-dark focus:outline-none ${low ? "text-red-700" : "text-matcha-dark"}`}
                      />
                      <span className="text-[10px] text-ink-soft uppercase tracking-[0.1em]">{r.unit}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{r.low_stock_threshold || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      data-testid={`ingredient-purchases-${r.id}`}
                      onClick={() => setPurchasingFor(r)}
                      className="text-xs uppercase tracking-[0.18em] text-matcha-dark hover:underline mr-3"
                    >
                      Purchases
                    </button>
                    <button data-testid={`adjust-stock-${r.id}`} onClick={() => setAdjustingFor(r)} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink mr-3">Restock</button>
                    <button data-testid={`use-stock-${r.id}`} onClick={() => setUsingFor(r)} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink mr-3">Use</button>
                    <button data-testid={`ingredient-del-${r.id}`} onClick={() => del(r.id)} className="text-ink-soft hover:text-red-600"><Trash2 size={16} /></button>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && <tr><td colSpan={12} className="p-8 text-center text-ink-soft">No ingredients in this view.</td></tr>}
          </tbody>
        </table>
      </div>
      {adding && <IngredientModal existing={rows} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {purchasingFor && (
        <PurchasesModal
          ingredient={purchasingFor}
          onClose={() => setPurchasingFor(null)}
          onChanged={load}
        />
      )}
      {adjustingFor && (
        <AdjustStockModal
          ingredient={adjustingFor}
          onClose={() => setAdjustingFor(null)}
          onSaved={() => { setAdjustingFor(null); load(); }}
        />
      )}
      {usingFor && (
        <UseStockModal
          ingredient={usingFor}
          onClose={() => setUsingFor(null)}
          onSaved={() => { setUsingFor(null); load(); }}
        />
      )}
    </div>
  );
}
function AdjustStockModal({ ingredient, onClose, onSaved }) {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [qty, setQty] = useState("");
  const [date, setDate] = useState(today);
  const [movements, setMovements] = useState([]);
  const [batchSku, setBatchSku] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/mgmt/ingredients/${ingredient.id}/movements`)
      .then((r) => setMovements(r.data))
      .catch(() => {});
  }, [ingredient.id]);

  // Preview next batch SKU whenever date changes
  useEffect(() => {
    api.get(`/mgmt/ingredients/${ingredient.id}/next-batch-sku`, { params: { date } })
      .then((r) => setBatchSku(r.data.batch_sku || ""))
      .catch(() => setBatchSku(""));
  }, [ingredient.id, date]);

  const qtyNum = Number(qty || 0);
  const delta = Math.abs(qtyNum); // always add stock
  const projected = Number(ingredient.stock_qty || 0) + delta;
  const userName = user?.preferred_name || user?.full_name || user?.email || "—";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!qtyNum || qtyNum <= 0) { setErr("Enter a quantity to add."); return; }
    setBusy(true);
    try {
      await api.post(`/mgmt/ingredients/${ingredient.id}/adjust`, {
        delta,
        reason: "Restock",
        date,
      });
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="adjust-stock-modal">
      <form onSubmit={submit} className="bg-bone w-full max-w-lg rounded-sm p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="overline text-ink-soft">Restock</p>
            <h3 className="font-display text-xl text-ink mt-1" style={{ letterSpacing: "0.05em" }}>{ingredient.name}</h3>
            <div className="flex items-center gap-2 mt-1.5">
              {ingredient.code && <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink bg-sage/40 px-1.5 py-0.5 rounded-sm">{ingredient.code}</span>}
              {ingredient.sku && <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-soft">{ingredient.sku}</span>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>

        {/* Current state */}
        <div className="grid grid-cols-3 gap-3 mb-6 p-4 bg-cream/40 border border-ink/10 rounded-sm">
          <div>
            <p className="overline text-ink-soft text-[10px]">Current</p>
            <p className="font-display text-2xl text-matcha-dark mt-1" data-testid="adjust-current-stock">
              {ingredient.stock_qty} <span className="text-sm text-ink-soft">{ingredient.unit}</span>
            </p>
          </div>
          <div>
            <p className="overline text-ink-soft text-[10px]">After restock</p>
            <p className="font-display text-2xl text-ink mt-1" data-testid="adjust-projected-stock">
              {projected.toFixed(3).replace(/\.?0+$/, "")} <span className="text-sm text-ink-soft">{ingredient.unit}</span>
            </p>
          </div>
          <div>
            <p className="overline text-ink-soft text-[10px]">Adding</p>
            <p className="font-display text-2xl text-matcha-dark mt-1" data-testid="adjust-delta">
              +{delta || 0}
            </p>
          </div>
        </div>

        {/* Qty + Date */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="overline text-ink-soft block mb-2">Quantity ({ingredient.unit})</label>
            <input
              data-testid="adjust-qty"
              type="number"
              step="0.001"
              min="0"
              required
              autoFocus
              placeholder="e.g. 5"
              className={inputCls + " w-full text-right font-mono text-lg"}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>
          <div>
            <label className="overline text-ink-soft block mb-2">Date</label>
            <input
              data-testid="adjust-date"
              type="date"
              required
              className={inputCls + " w-full"}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>

        {/* Auto Batch SKU preview */}
        {batchSku && (
          <div className="mb-4 p-3 bg-sage/20 border border-matcha-dark/20 rounded-sm flex items-center justify-between gap-3" data-testid="adjust-batch-sku-row">
            <div>
              <p className="overline text-ink-soft text-[10px]">Auto Batch SKU</p>
              <p className="font-mono text-sm text-ink mt-0.5" data-testid="adjust-batch-sku">{batchSku}</p>
            </div>
            <p className="text-[10px] text-ink-soft text-right max-w-[55%]">
              Generated from date · code · running number. Resets each month.
            </p>
          </div>
        )}

        {/* Attribution */}
        <p className="text-[11px] text-ink-soft" data-testid="adjust-attribution">
          Reason <span className="text-ink">Restock</span> · Recorded by <span className="text-ink">{userName}</span> · {date === today ? "Today" : date}
        </p>

        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mt-3" data-testid="adjust-err">{err}</p>}

        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">Cancel</button>
          <button
            data-testid="adjust-submit"
            disabled={busy || !qtyNum || qtyNum <= 0}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50"
          >
            {busy ? "Saving…" : "Confirm restock"}
          </button>
        </div>

        {movements.length > 0 && (
          <div className="mt-8 pt-6 border-t border-ink/10">
            <p className="overline text-ink-soft mb-3">Recent movements</p>
            <ul className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="adjust-movements">
              {movements.slice(0, 8).map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-ink-soft truncate">
                    {m.date || (m.created_at || "").slice(0, 10)} · {m.by_name || "—"}
                    {m.batch_sku && <span className="font-mono text-ink/70"> · {m.batch_sku}</span>}
                  </span>
                  <span className={`font-mono whitespace-nowrap ${m.delta > 0 ? "text-matcha-dark" : "text-red-700"}`}>
                    {m.delta > 0 ? "+" : ""}{m.delta} {ingredient.unit}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </form>
    </div>
  );
}



function UseStockModal({ ingredient, onClose, onSaved }) {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("Usage");
  const [date, setDate] = useState(today);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const qtyNum = Number(qty || 0);
  const projected = Math.max(0, Number(ingredient.stock_qty || 0) - qtyNum);
  const overdraw = qtyNum > Number(ingredient.stock_qty || 0);
  const userName = user?.preferred_name || user?.full_name || user?.email || "—";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!qtyNum || qtyNum <= 0) { setErr("Enter a quantity to use."); return; }
    if (overdraw && !window.confirm(`This will deduct more than the current stock (${ingredient.stock_qty}). Continue?`)) return;
    setBusy(true);
    try {
      await api.post(`/mgmt/ingredients/${ingredient.id}/adjust`, {
        delta: -qtyNum,
        reason,
        date,
      });
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="use-stock-modal">
      <form onSubmit={submit} className="bg-bone w-full max-w-md rounded-sm p-6 sm:p-8">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="overline text-red-700">Use / Deduct</p>
            <h3 className="font-display text-xl text-ink mt-1" style={{ letterSpacing: "0.05em" }}>{ingredient.name}</h3>
            <div className="flex items-center gap-2 mt-1.5">
              {ingredient.code && <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink bg-sage/40 px-1.5 py-0.5 rounded-sm">{ingredient.code}</span>}
              {ingredient.sku && <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-soft">{ingredient.sku}</span>}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5 p-4 bg-cream/40 border border-ink/10 rounded-sm">
          <div>
            <p className="overline text-ink-soft text-[10px]">Current</p>
            <p className="font-display text-2xl text-matcha-dark mt-1" data-testid="use-current-stock">
              {ingredient.stock_qty} <span className="text-sm text-ink-soft">{ingredient.unit}</span>
            </p>
          </div>
          <div>
            <p className="overline text-ink-soft text-[10px]">After use</p>
            <p className={`font-display text-2xl mt-1 ${overdraw ? "text-red-700" : "text-ink"}`} data-testid="use-projected-stock">
              {projected.toFixed(3).replace(/\.?0+$/, "")} <span className="text-sm text-ink-soft">{ingredient.unit}</span>
            </p>
          </div>
          <div>
            <p className="overline text-ink-soft text-[10px]">Using</p>
            <p className="font-display text-2xl text-red-700 mt-1" data-testid="use-delta">
              −{qtyNum || 0}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="overline text-ink-soft block mb-2">Quantity ({ingredient.unit})</label>
            <input
              data-testid="use-qty"
              type="number"
              step="0.001"
              min="0"
              required
              autoFocus
              className={inputCls + " w-full text-right"}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>
          <div>
            <label className="overline text-ink-soft block mb-2">Date</label>
            <input data-testid="use-date" required type="date" className={inputCls + " w-full"} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>

        <div className="mb-5">
          <label className="overline text-ink-soft block mb-2">Reason</label>
          <input
            data-testid="use-reason"
            list="use-reason-list"
            className={inputCls + " w-full"}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <datalist id="use-reason-list">
            <option value="Usage" />
            <option value="Wastage" />
            <option value="Spoiled" />
            <option value="Correction" />
            <option value="Sample" />
          </datalist>
        </div>

        <p className="text-[10px] text-ink-soft mb-4">Recording as {userName}</p>

        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mb-4">{err}</p>}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">Cancel</button>
          <button
            data-testid="use-confirm"
            disabled={busy || !qtyNum || qtyNum <= 0}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-red-700 text-bone text-xs uppercase tracking-[0.22em] hover:bg-red-800 transition-colors disabled:opacity-50"
          >
            {busy ? "Saving…" : `Confirm · −${qtyNum || 0} ${ingredient.unit}`}
          </button>
        </div>
      </form>
    </div>
  );
}


function PurchasesModal({ ingredient, onClose, onChanged }) {
  const today = new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ date: today, qty: "", unit_cost: "", supplier: "", notes: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/mgmt/ingredients/${ingredient.id}/purchases`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ingredient.id]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post(`/mgmt/ingredients/${ingredient.id}/purchases`, {
        date: form.date,
        qty: Number(form.qty),
        unit_cost: Number(form.unit_cost),
        supplier: form.supplier,
        notes: form.notes,
      });
      setForm({ date: today, qty: "", unit_cost: "", supplier: "", notes: "" });
      await load();
      onChanged();
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (pid) => {
    if (!window.confirm("Delete this purchase record? Stock will be reverted.")) return;
    await api.delete(`/mgmt/ingredients/${ingredient.id}/purchases/${pid}`);
    await load();
    onChanged();
  };

  const totalQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);
  const totalSpent = rows.reduce((s, r) => s + Number(r.total_cost || 0), 0);

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="purchases-modal">
      <div className="bg-bone w-full max-w-3xl rounded-sm p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>{ingredient.name} · Purchases</h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <p className="text-sm text-ink-soft mb-6">
          Stock now: <span className="text-matcha-dark">{ingredient.stock_qty} {ingredient.unit}</span>
          <span className="mx-2">·</span>
          Cost/pack (weighted avg): <span className="text-matcha-dark">RM {Number(ingredient.cost_per_unit || 0).toFixed(4)}</span>
          {(() => {
            const pk = Number(ingredient.serves || 1) || 1;
            if (pk === 1) return null;
            const perUnit = Number(ingredient.cost_per_unit || 0) / pk;
            const measure = ["kg","g"].includes(ingredient.unit) ? "g"
                          : ["l","litre","ml"].includes(ingredient.unit) ? "ml" : "piece";
            return (
              <>
                <span className="mx-2">·</span>
                Cost/unit: <span className="text-matcha-dark">RM {perUnit.toFixed(4)} / {measure}</span>
              </>
            );
          })()}
        </p>

        <form onSubmit={submit} className="bg-cream/40 border border-ink/10 rounded-sm p-4 mb-6">
          <p className="overline text-ink-soft mb-3">Record New Purchase</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <div>
              <label className="overline text-ink-soft block mb-1">Date</label>
              <input data-testid="purchase-date" required type="date" className={inputCls + " w-full"} value={form.date} onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))} />
            </div>
            <div>
              <label className="overline text-ink-soft block mb-1">Qty ({ingredient.unit})</label>
              <input data-testid="purchase-qty" required type="number" step="0.001" min="0" className={inputCls + " w-full"} value={form.qty} onChange={(e) => setForm((s) => ({ ...s, qty: e.target.value }))} />
            </div>
            <div>
              <label className="overline text-ink-soft block mb-1">Cost / {ingredient.unit} (RM)</label>
              <input data-testid="purchase-unit-cost" required type="number" step="0.0001" min="0" className={inputCls + " w-full"} value={form.unit_cost} onChange={(e) => setForm((s) => ({ ...s, unit_cost: e.target.value }))} />
            </div>
            <div>
              <label className="overline text-ink-soft block mb-1">Supplier</label>
              <input data-testid="purchase-supplier" className={inputCls + " w-full"} value={form.supplier} onChange={(e) => setForm((s) => ({ ...s, supplier: e.target.value }))} placeholder="Optional" />
            </div>
            <div>
              <label className="overline text-ink-soft block mb-1">Notes</label>
              <input data-testid="purchase-notes" className={inputCls + " w-full"} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          {form.qty && form.unit_cost && (
            <p className="text-xs text-ink-soft mb-3">
              Total: <span className="text-matcha-dark">RM {(Number(form.qty) * Number(form.unit_cost)).toFixed(2)}</span>
            </p>
          )}
          {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mb-3">{err}</p>}
          <button data-testid="purchase-submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full px-5 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50">
            {busy ? "Adding…" : "+ Add Purchase"}
          </button>
        </form>

        <div className="bg-bone border border-ink/10 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sage/40">
              <tr className="text-left text-ink-soft">
                <th className="px-3 py-2 overline">Date</th>
                <th className="px-3 py-2 overline text-right">Qty</th>
                <th className="px-3 py-2 overline text-right">Unit Cost</th>
                <th className="px-3 py-2 overline text-right">Total</th>
                <th className="px-3 py-2 overline">Supplier</th>
                <th className="px-3 py-2 overline">Notes</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-ink/5" data-testid={`purchase-row-${p.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{p.date}</td>
                  <td className="px-3 py-2 text-right">{p.qty} {ingredient.unit}</td>
                  <td className="px-3 py-2 text-right">RM {Number(p.unit_cost).toFixed(4)}</td>
                  <td className="px-3 py-2 text-right text-matcha-dark">RM {Number(p.total_cost).toFixed(2)}</td>
                  <td className="px-3 py-2 text-ink-soft">{p.supplier || "—"}</td>
                  <td className="px-3 py-2 text-ink-soft">{p.notes || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`purchase-del-${p.id}`} onClick={() => del(p.id)} className="text-ink-soft hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-ink-soft text-sm">No purchases yet.</td></tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-ink/15 bg-cream/50">
                  <td className="px-3 py-2 overline text-ink">Total</td>
                  <td className="px-3 py-2 text-right">{totalQty} {ingredient.unit}</td>
                  <td></td>
                  <td className="px-3 py-2 text-right font-medium text-matcha-dark">RM {totalSpent.toFixed(2)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function IngredientModal({ existing = [], onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", code: "", sku: "", kind: "physical", unit: "g", cost_per_unit: "", stock_qty: "", low_stock_threshold: "" });
  const [err, setErr] = useState("");
  // When the admin types a name that matches an existing ingredient (case-
  // insensitive), pre-fill the rest of the form so they can inspect or
  // duplicate its shape. Prevents accidental duplicates like "Fresh Milk"
  // and "fresh milk" both getting created.
  const matched = useMemo(() => {
    const n = (form.name || "").trim().toLowerCase();
    if (!n) return null;
    return existing.find((x) => (x.name || "").trim().toLowerCase() === n) || null;
  }, [form.name, existing]);
  const pickExisting = (ing) => {
    setForm({
      name: ing.name || "",
      code: ing.code || "",
      sku: ing.sku || "",
      kind: ing.kind || "physical",
      unit: ing.unit || "g",
      cost_per_unit: String(ing.cost_per_unit ?? ""),
      stock_qty: String(ing.stock_qty ?? ""),
      low_stock_threshold: String(ing.low_stock_threshold ?? ""),
    });
  };
  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/mgmt/ingredients", {
        name: form.name,
        code: form.code.trim(),
        sku: form.sku.trim(),
        kind: form.kind,
        unit: form.unit,
        cost_per_unit: Number(form.cost_per_unit || 0),
        stock_qty: Number(form.stock_qty || 0),
        low_stock_threshold: Number(form.low_stock_threshold || 0),
      });
      onSaved();
    } catch (e2) { setErr(formatErr(e2?.response?.data?.detail) || e2.message); }
  };
  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="ingredient-modal">
      <form onSubmit={save} className="bg-bone w-full max-w-md rounded-sm p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>New Ingredient</h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div><label className="overline text-ink-soft block mb-2">Name</label>
            <input
              data-testid="ing-form-name"
              required
              list="ing-existing-names"
              placeholder="Type or pick from existing…"
              className={inputCls + " w-full"}
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              onBlur={() => { if (matched) pickExisting(matched); }}
            />
            <datalist id="ing-existing-names">
              {existing.map((x) => (
                <option key={x.id} value={x.name}>{x.code || x.sku || ""}</option>
              ))}
            </datalist>
            {matched && (
              <p className="text-[11px] text-amber-800 mt-1" data-testid="ing-form-match-hint">
                Already exists as <span className="font-mono">{matched.code || matched.sku || matched.id.slice(0,8)}</span>.
                Saving will create a duplicate — <button type="button" className="underline hover:text-matcha-dark" onClick={() => pickExisting(matched)}>load its details</button> or change the name.
              </p>
            )}
          </div>
          <div><label className="overline text-ink-soft block mb-2">Kind</label>
            <select data-testid="ing-form-kind" className={inputCls + " w-full"} value={form.kind} onChange={(e) => {
              const k = e.target.value;
              setForm((s) => ({
                ...s,
                kind: k,
                // "Piece" always counts per single item, so auto-flip the
                // unit to `piece` (admin can still override afterwards).
                unit: k === "piece" ? "piece" : s.unit,
              }));
            }}>
              <option value="physical">Physical Items</option>
              <option value="component">Daily Components</option>
              <option value="ingredient">Daily Ingredients</option>
              <option value="matcha">Matcha & Hojicha Powder</option>
              <option value="cups">Cups</option>
              <option value="piece">Piece</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline text-ink-soft block mb-2">Code <span className="text-ink-soft/60 normal-case tracking-normal">(auto if blank)</span></label>
              <input data-testid="ing-form-code" placeholder="auto · e.g. TAM" className={inputCls + " w-full font-mono"} value={form.code} onChange={(e) => setForm((s) => ({ ...s, code: e.target.value.toUpperCase() }))} />
            </div>
            <div><label className="overline text-ink-soft block mb-2">SKU <span className="text-ink-soft/60 normal-case tracking-normal">(auto if blank)</span></label>
              <input data-testid="ing-form-sku" placeholder="auto · MS-YYMM-B01-XXX-01" className={inputCls + " w-full font-mono"} value={form.sku} onChange={(e) => setForm((s) => ({ ...s, sku: e.target.value.toUpperCase() }))} />
            </div>
            <div><label className="overline text-ink-soft block mb-2">Unit</label>
              <select data-testid="ing-form-unit" className={inputCls + " w-full"} value={form.unit} onChange={(e) => setForm((s) => ({ ...s, unit: e.target.value }))}>
                {UNITS.map((u) => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div><label className="overline text-ink-soft block mb-2">Cost / unit (RM)</label>
              <input data-testid="ing-form-cost" type="number" step="0.0001" className={inputCls + " w-full"} value={form.cost_per_unit} onChange={(e) => setForm((s) => ({ ...s, cost_per_unit: e.target.value }))} />
            </div>
            <div><label className="overline text-ink-soft block mb-2">Stock qty</label>
              <input data-testid="ing-form-stock" type="number" step="0.001" className={inputCls + " w-full"} value={form.stock_qty} onChange={(e) => setForm((s) => ({ ...s, stock_qty: e.target.value }))} />
            </div>
            <div><label className="overline text-ink-soft block mb-2">Low threshold</label>
              <input data-testid="ing-form-threshold" type="number" step="0.001" className={inputCls + " w-full"} value={form.low_stock_threshold} onChange={(e) => setForm((s) => ({ ...s, low_stock_threshold: e.target.value }))} />
            </div>
          </div>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mt-4">{err}</p>}
        <div className="flex justify-end gap-3 mt-8">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">Cancel</button>
          <button data-testid="ing-save" className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors">Save</button>
        </div>
      </form>
    </div>
  );
}


const PERIODS = ["daily", "weekly", "monthly", "yearly"];
const OPEX_CATEGORIES = [
  "Premises",
  "Utilities",
  "Marketing",
  "Software & Subscriptions",
  "Supplies",
  "Insurance",
  "Licenses & Permits",
  "Equipment",
  "Bank & Finance",
  "Transport & Delivery",
  "Professional Fees",
  "Staff Welfare",
  "Miscellaneous",
];

function OpexTab() {
  const [view, setView] = useState("items"); // "items" | "monthly"
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [billsFor, setBillsFor] = useState(null);
  const load = async () => {
    const { data } = await api.get("/mgmt/operating-costs");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete operating cost?")) return;
    await api.delete(`/mgmt/operating-costs/${id}`); load();
  };

  const dailyEquiv = (r) => {
    if (r.variable) return null; // varies by month → show "—"
    const a = Number(r.amount || 0);
    if (r.period === "daily") return a;
    if (r.period === "weekly") return a / 7;
    if (r.period === "monthly") return a / 30;
    if (r.period === "yearly") return a / 365;
    return 0;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2 bg-cream/60 border border-ink/10 rounded-full p-1">
          <button
            data-testid="opex-view-items"
            onClick={() => setView("items")}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] transition-colors ${view === "items" ? "bg-ink text-bone" : "text-ink-soft hover:text-ink"}`}
          >
            Items
          </button>
          <button
            data-testid="opex-view-monthly"
            onClick={() => setView("monthly")}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] transition-colors ${view === "monthly" ? "bg-ink text-bone" : "text-ink-soft hover:text-ink"}`}
          >
            By Month
          </button>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-ink-soft text-sm hidden md:block">{rows.length} entries · pro-rated daily into Net Profit</p>
          <button data-testid="add-opex-btn" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 rounded-full px-5 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors">
            <Plus size={14} /> Add Operating Cost
          </button>
        </div>
      </div>
      {view === "items" ? (
        <div className="bg-bone border border-ink/10 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-sage/40">
              <tr className="text-left text-ink-soft">
                <th className="px-4 py-3 overline">Name</th>
                <th className="px-4 py-3 overline">Category</th>
                <th className="px-4 py-3 overline">Type</th>
                <th className="px-4 py-3 overline">Amount</th>
                <th className="px-4 py-3 overline">Period</th>
                <th className="px-4 py-3 overline">~ Daily</th>
                <th className="px-4 py-3 overline">Active From</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const de = dailyEquiv(r);
                return (
                  <tr key={r.id} className="border-t border-ink/5" data-testid={`opex-row-${r.id}`}>
                    <td className="px-4 py-3">{r.name}</td>
                    <td className="px-4 py-3 text-ink-soft">{r.category || "—"}</td>
                    <td className="px-4 py-3">
                      {r.variable ? (
                        <span className="text-xs uppercase tracking-[0.18em] text-matcha-dark">Variable</span>
                      ) : (
                        <span className="text-xs uppercase tracking-[0.18em] text-ink-soft">Fixed</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{r.variable ? <span className="text-ink-soft">—</span> : fmt(r.amount)}</td>
                    <td className="px-4 py-3 text-ink-soft">{r.period}</td>
                    <td className="px-4 py-3 text-matcha-dark">{de === null ? "—" : fmt(de)}</td>
                    <td className="px-4 py-3 text-ink-soft">{r.start_date || "—"}{r.end_date ? ` → ${r.end_date}` : ""}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {r.variable && (
                        <button
                          data-testid={`opex-bills-${r.id}`}
                          onClick={() => setBillsFor(r)}
                          className="text-xs uppercase tracking-[0.18em] text-matcha-dark hover:underline mr-3"
                        >
                          Monthly Bills
                        </button>
                      )}
                      <button data-testid={`opex-edit-${r.id}`} onClick={() => setEditing(r)} className="text-xs uppercase tracking-[0.18em] text-matcha-dark hover:underline mr-3">Edit</button>
                      <button data-testid={`opex-del-${r.id}`} onClick={() => del(r.id)} className="text-ink-soft hover:text-red-600"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-ink-soft">No operating costs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <OpexMonthlyMatrix rows={rows} />
      )}
      {(adding || editing) && (
        <OpexModal
          item={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); load(); }}
        />
      )}
      {billsFor && (
        <OpexBillsModal
          item={billsFor}
          onClose={() => setBillsFor(null)}
        />
      )}
    </div>
  );
}

// Packaging & Add-ons — small panel under the Operating Costs list. Lets
// the admin tick which ingredients ship with each channel (e.g. Grab uses
// bag + bottle; Walk-in uses bag). Per-cup cost flows into the P&L COGS
// via `channel_addons` server-side, so packaging is never double-counted.
function ChannelAddonsPanel() {
  const [data, setData] = useState(null); // { map, resolved, catalog }
  const [allIngredients, setAllIngredients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const load = async () => {
    try {
      const [{ data: d }, { data: all }] = await Promise.all([
        api.get("/mgmt/channel-addons"),
        api.get("/mgmt/ingredients"),
      ]);
      setData(d);
      setAllIngredients(all || []);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const channels = Object.keys(data.map || { grab: [], walk_in: [] });
  if (!channels.length) channels.push("grab", "walk_in");
  const toggle = async (channel, ingId) => {
    const nextMap = { ...(data.map || {}) };
    const cur = new Set(nextMap[channel] || []);
    if (cur.has(ingId)) cur.delete(ingId); else cur.add(ingId);
    nextMap[channel] = [...cur];
    setData({ ...data, map: nextMap });
    setSaving(true);
    try {
      await api.put("/mgmt/channel-addons", { map: nextMap });
      await load(); // pull fresh resolved names + costs
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };
  // "+ Add SKU" — takes any ingredient in the catalog, tags it as
  // packaging (if not already), and slots it into the channel. Saves
  // the ingredients tab a round-trip: the admin never has to leave the
  // Operating Costs screen just to expose a new packaging SKU.
  const addSku = async (channel, ingId) => {
    if (!ingId) return;
    setSaving(true);
    try {
      const target = allIngredients.find((x) => x.id === ingId);
      // Tagging as packaging via this shortcut also normalises the unit
      // to "piece" — the P&L per-cup formula multiplies `cups × cost/unit`
      // so the ingredient's unit has to be per-piece, not "pack" or "box".
      // If the admin needs per-pack semantics they can flip the unit back
      // on the Ingredients tab.
      const patch = {};
      if (target && target.kind !== "packaging") patch.kind = "packaging";
      if (target && target.unit && target.unit !== "piece") patch.unit = "piece";
      if (Object.keys(patch).length) {
        await api.patch(`/mgmt/ingredients/${ingId}`, patch);
      }
      const nextMap = { ...(data.map || {}) };
      const cur = new Set(nextMap[channel] || []);
      cur.add(ingId);
      nextMap[channel] = [...cur];
      await api.put("/mgmt/channel-addons", { map: nextMap });
      await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };
  // Untag an ingredient completely — removes it from every channel AND
  // flips its kind back to "physical" so it disappears from the packaging
  // picker on Operating Costs. Used by the trash icon on each chip when
  // the admin no longer treats that SKU as packaging at all.
  const untag = async (ingId) => {
    if (!window.confirm("Remove this SKU from the packaging picker? It stays in the ingredients catalogue but stops appearing here.")) return;
    setSaving(true);
    try {
      const nextMap = { ...(data.map || {}) };
      for (const ch of Object.keys(nextMap)) {
        nextMap[ch] = (nextMap[ch] || []).filter((i) => i !== ingId);
      }
      await api.patch(`/mgmt/ingredients/${ingId}`, { kind: "physical" });
      await api.put("/mgmt/channel-addons", { map: nextMap });
      await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };
  const label = (ch) => ch.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <section className="mt-10" data-testid="channel-addons-panel">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
        <div>
          <p className="overline text-matcha-dark">Packaging &amp; Add-ons</p>
          <h3 className="text-lg text-ink mt-1">Per-cup packaging cost by channel</h3>
          <p className="text-xs text-ink-soft mt-1 max-w-2xl">
            Tick the ingredients that ship with every drink on each channel, or use "+ Add SKU"
            to pull any ingredient into the picker (it gets tagged as Packaging automatically).
            Cost per unit follows your purchases.
          </p>
        </div>
        {saving && <span className="text-xs text-ink-soft">Saving…</span>}
      </div>
      {err && <p className="text-red-700 text-xs mb-3">{err}</p>}
      <div className="bg-bone border border-ink/10 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-sage/40">
            <tr className="text-left text-ink-soft">
              <th className="px-4 py-3 overline">Channel</th>
              <th className="px-4 py-3 overline">Assigned ingredients (per cup)</th>
              <th className="px-4 py-3 overline text-right">Sum per cup</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((ch) => {
              const assigned = new Set(data.map[ch] || []);
              const sum = (data.resolved?.[ch] || []).reduce((a, x) => {
                // Use the derived per-unit cost so the sum matches what
                // the P&L per-cup COGS actually multiplies (cost_per_unit
                // ÷ pack size). `cost_per_measure` comes from the backend.
                if (x.cost_per_measure != null) return a + Number(x.cost_per_measure || 0);
                const pk = Number(x.serves || 1) || 1;
                return a + (Number(x.cost_per_unit || 0) / pk);
              }, 0);
              // Anything not already packaging + not already ticked here is
              // fair game for the + Add SKU dropdown. Packaging-tagged items
              // show as chips already, so hide them from the "Add" list to
              // avoid duplicates.
              const addable = allIngredients.filter((ing) => ing.kind !== "packaging" && !assigned.has(ing.id));
              return (
                <tr key={ch} className="border-t border-ink/5" data-testid={`channel-row-${ch}`}>
                  <td className="px-4 py-3 uppercase tracking-[0.18em] text-xs text-matcha-dark whitespace-nowrap">{label(ch)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2 items-center">
                      {data.catalog.map((ing) => {
                        const active = assigned.has(ing.id);
                        return (
                          <span
                            key={ing.id}
                            className={`group inline-flex items-center rounded-full border transition-colors text-[11px] uppercase tracking-[0.18em] ${
                              active
                                ? "text-matcha-dark border-matcha-dark bg-sage/25"
                                : "text-ink-soft border-ink/15 hover:border-matcha-dark/60"
                            }`}
                          >
                            <button
                              type="button"
                              data-testid={`channel-${ch}-toggle-${ing.id}`}
                              onClick={() => toggle(ch, ing.id)}
                              title={ing.sku || ing.code || ""}
                              className="pl-3 pr-1 py-1"
                            >
                              {ing.name}
                              {(ing.sku || ing.code) && (
                                <span className="ml-1 font-mono opacity-60 normal-case tracking-normal">· {ing.sku || ing.code}</span>
                              )}
                              {(() => {
                                // Per-unit price — the number recipes and
                                // the P&L multiply by cups sold. Prefer
                                // backend-derived `cost_per_measure`;
                                // fall back to local math.
                                const pk = Number(ing.serves || 1) || 1;
                                const perUnit = ing.cost_per_measure != null
                                  ? Number(ing.cost_per_measure)
                                  : Number(ing.cost_per_unit || 0) / pk;
                                const measure = ["kg","g"].includes(ing.unit) ? "g"
                                              : ["l","litre","ml"].includes(ing.unit) ? "ml"
                                              : "piece";
                                return (
                                  <span className="ml-1 opacity-70">
                                    RM {perUnit.toFixed(4)} / {measure}
                                  </span>
                                );
                              })()}
                            </button>
                            {active ? (
                              <button
                                type="button"
                                data-testid={`channel-${ch}-remove-${ing.id}`}
                                onClick={() => toggle(ch, ing.id)}
                                title="Remove from this channel"
                                className="px-2 py-1 rounded-r-full hover:bg-matcha-dark/10 text-matcha-dark"
                              >
                                ×
                              </button>
                            ) : (
                              <button
                                type="button"
                                data-testid={`channel-${ch}-untag-${ing.id}`}
                                onClick={() => untag(ing.id)}
                                title="Untag SKU as packaging (removes from picker)"
                                className="px-2 py-1 rounded-r-full text-ink-soft/60 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Trash2 size={11} />
                              </button>
                            )}
                          </span>
                        );
                      })}
                      <select
                        data-testid={`channel-${ch}-add-sku`}
                        value=""
                        disabled={saving || addable.length === 0}
                        onChange={(e) => addSku(ch, e.target.value)}
                        className="text-[11px] uppercase tracking-[0.18em] px-3 py-1 rounded-full border border-dashed border-matcha-dark/50 text-matcha-dark bg-transparent hover:bg-sage/20 focus:outline-none cursor-pointer"
                      >
                        <option value="">+ Add SKU…</option>
                        {addable.map((ing) => {
                          const pk = Number(ing.serves || 1) || 1;
                          const perUnit = Number(ing.cost_per_unit || 0) / pk;
                          return (
                            <option key={ing.id} value={ing.id}>
                              {ing.name}{(ing.sku || ing.code) ? ` · ${ing.sku || ing.code}` : ""} · RM {perUnit.toFixed(4)}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-matcha-dark whitespace-nowrap">RM {sum.toFixed(4)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function OpexMonthlyMatrix({ onOpenBills, rows }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null); // "YYYY-MM"
  // Local input cache: { "costId|YYYY-MM": "1234.50" } — strings to allow empty
  const [edits, setEdits] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = async (yr = year) => {
    setLoading(true);
    setErr("");
    try {
      const { data: d } = await api.get("/mgmt/operating-costs/monthly-summary", { params: { year: yr } });
      setData(d);
      // Reset local edits from server values
      const next = {};
      d.items.forEach((it) => {
        if (it.variable) {
          d.months.forEach((m) => {
            next[`${it.id}|${m}`] = it.months[m] > 0 ? String(it.months[m]) : "";
          });
        }
      });
      setEdits(next);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year, rows.length]);

  const monthFull = (m) => {
    const labels = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return labels[parseInt(m.slice(5, 7), 10) - 1];
  };

  const saveBill = async (costId, month) => {
    const key = `${costId}|${month}`;
    const raw = edits[key];
    setErr("");
    setSavingKey(key);
    try {
      if (raw === "" || raw == null) {
        await api.delete(`/mgmt/operating-costs/${costId}/bills/${month}`);
      } else {
        await api.put(`/mgmt/operating-costs/${costId}/bills/${month}`, { amount: Number(raw) });
      }
      // Refresh totals
      await load(year);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSavingKey(null);
    }
  };

  // Copy the amount typed into the "current" cell across every month
  // that has POS revenue. Backend skips months with an existing manual
  // bill unless the admin confirms overwrite. Instant "type once, fill
  // every real month" for Rent, Wifi, etc.
  const applyToRevenueMonths = async (costId, month) => {
    const key = `${costId}|${month}`;
    const raw = edits[key];
    const amt = Number(raw);
    if (!Number.isFinite(amt) || amt <= 0) {
      window.alert("Type an amount in this month's cell first, then click Apply.");
      return;
    }
    let overwrite = false;
    try {
      // First pass — non-destructive
      let res = await api.post(`/mgmt/operating-costs/${costId}/bills/apply-to-revenue-months`, {
        amount: amt, source_month: month, overwrite: false,
      });
      if ((res.data.skipped || []).length > 0) {
        const skip = res.data.skipped;
        overwrite = window.confirm(
          `Applied to ${res.data.applied.length} month(s). ` +
          `${skip.length} already has a manual bill (${skip.slice(0,4).join(", ")}${skip.length>4?"…":""}). ` +
          `Overwrite them too?`
        );
        if (overwrite) {
          res = await api.post(`/mgmt/operating-costs/${costId}/bills/apply-to-revenue-months`, {
            amount: amt, source_month: month, overwrite: true,
          });
        }
      }
      await load(year);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    }
  };

  return (
    <div data-testid="opex-monthly-matrix">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <label className="overline text-ink-soft">Year</label>
        <input
          data-testid="opex-monthly-year"
          type="number"
          min="2020"
          max="2100"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className={inputCls + " w-28"}
        />
        {data && (
          <p className="ml-auto text-sm text-ink-soft">
            Year total: <span className="text-matcha-dark">{fmt(data.year_total)}</span>
          </p>
        )}
      </div>
      {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mb-4">{err}</p>}
      {loading || !data ? (
        <p className="text-ink-soft text-sm">Loading…</p>
      ) : (
        <div className="bg-bone border border-ink/10 rounded-sm divide-y divide-ink/5">
          {data.months.map((m) => {
            const open = expanded === m;
            const total = data.totals_by_month[m] || 0;
            const itemsThisMonth = data.items.filter((it) => it.months[m] > 0).length;
            return (
              <div key={m} data-testid={`opex-month-${m}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(open ? null : m)}
                  data-testid={`opex-month-toggle-${m}`}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-cream/40 transition-colors text-left"
                >
                  <div className="flex items-center gap-4">
                    <span className={`text-xs uppercase tracking-[0.18em] transition-transform inline-block ${open ? "rotate-90 text-matcha-dark" : "text-ink-soft"}`}>›</span>
                    <div>
                      <p className="font-display text-lg text-ink" style={{ letterSpacing: "0.04em" }}>
                        {monthFull(m)} <span className="text-ink-soft text-sm">{year}</span>
                      </p>
                      <p className="text-xs text-ink-soft mt-0.5">{itemsThisMonth} item{itemsThisMonth === 1 ? "" : "s"} recorded</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="overline text-ink-soft">Total</p>
                    <p className={`font-display text-xl ${total > 0 ? "text-matcha-dark" : "text-ink-soft"}`} style={{ letterSpacing: "0.02em" }}>
                      {fmt(total)}
                    </p>
                  </div>
                </button>
                {open && (
                  <div className="px-5 pb-5 bg-cream/30 border-t border-ink/5" data-testid={`opex-month-panel-${m}`}>
                    <table className="w-full text-sm mt-4">
                      <thead>
                        <tr className="text-left text-ink-soft">
                          <th className="py-2 overline w-1/3">Item</th>
                          <th className="py-2 overline">Category</th>
                          <th className="py-2 overline">Type</th>
                          <th className="py-2 overline text-right">Amount (RM)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.items.map((it) => {
                          const key = `${it.id}|${m}`;
                          const isSaving = savingKey === key;
                          return (
                            <tr key={it.id} className="border-t border-ink/5" data-testid={`opex-month-${m}-item-${it.id}`}>
                              <td className="py-3 text-ink">{it.name}</td>
                              <td className="py-3 text-ink-soft">{it.category || "—"}</td>
                              <td className="py-3">
                                <span className={`text-xs uppercase tracking-[0.18em] ${it.variable ? "text-matcha-dark" : "text-ink-soft"}`}>
                                  {it.variable ? "Variable" : "Fixed"}
                                </span>
                              </td>
                              <td className="py-3 text-right">
                                {it.variable ? (
                                  <div className="flex items-center justify-end gap-2">
                                    <input
                                      data-testid={`opex-month-input-${m}-${it.id}`}
                                      type="number"
                                      step="0.01"
                                      placeholder="0.00"
                                      value={edits[key] ?? ""}
                                      onChange={(e) => setEdits((s) => ({ ...s, [key]: e.target.value }))}
                                      onBlur={() => saveBill(it.id, m)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.preventDefault();
                                          e.currentTarget.blur();
                                        }
                                      }}
                                      className={inputCls + " w-32 text-right"}
                                    />
                                    <button
                                      type="button"
                                      data-testid={`opex-apply-all-${m}-${it.id}`}
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => applyToRevenueMonths(it.id, m)}
                                      title="Copy this amount to every month with POS revenue"
                                      className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-matcha-dark/40 text-matcha-dark hover:bg-matcha-dark hover:text-bone transition-colors"
                                    >
                                      Apply to all
                                    </button>
                                    {isSaving && <span className="text-[10px] text-ink-soft uppercase tracking-[0.18em]">Saving…</span>}
                                  </div>
                                ) : (
                                  <span className={it.months[m] > 0 ? "text-ink" : "text-ink-soft/50"}>
                                    {it.months[m] > 0 ? fmt(it.months[m]) : "—"}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {data.items.length === 0 && (
                          <tr><td colSpan={4} className="py-6 text-center text-ink-soft">No operating costs configured.</td></tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-ink/15">
                          <td colSpan={3} className="pt-3 overline text-ink">Month Total</td>
                          <td className="pt-3 text-right font-display text-lg text-matcha-dark" data-testid={`opex-month-total-${m}`}>{fmt(total)}</td>
                        </tr>
                      </tfoot>
                    </table>
                    <p className="text-xs text-ink-soft mt-3">Tip: type the actual bill amount for variable items and press Enter or click outside to save. Fixed items use their configured monthly amount.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OpexModal({ item, onClose, onSaved }) {
  const isNew = !item;
  const [form, setForm] = useState({
    name: item?.name || "",
    category: item?.category || "",
    amount: item?.amount ?? "",
    period: item?.period || "monthly",
    variable: item?.variable || false,
    start_date: item?.start_date || "",
    end_date: item?.end_date || "",
    notes: item?.notes || "",
  });
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = {
        name: form.name,
        category: form.category,
        amount: form.variable ? 0 : Number(form.amount),
        period: form.variable ? "monthly" : form.period,
        variable: !!form.variable,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        notes: form.notes,
      };
      if (isNew) await api.post("/mgmt/operating-costs", payload);
      else await api.patch(`/mgmt/operating-costs/${item.id}`, payload);
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    }
  };
  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="opex-modal">
      <form onSubmit={save} className="bg-bone w-full max-w-md rounded-sm p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>
            {isNew ? "New Operating Cost" : "Edit Operating Cost"}
          </h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div><label className="overline text-ink-soft block mb-2">Name</label>
            <input data-testid="opex-form-name" required className={inputCls + " w-full"} value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="Rent, Utilities, WiFi…" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              data-testid="opex-form-variable"
              type="checkbox"
              checked={!!form.variable}
              onChange={(e) => setForm((s) => ({ ...s, variable: e.target.checked }))}
              className="w-4 h-4 accent-matcha-dark"
            />
            <span className="text-sm text-ink">
              Variable monthly cost
              <span className="block text-xs text-ink-soft mt-0.5">e.g. Utilities, Gas — record actual bill each month</span>
            </span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline text-ink-soft block mb-2">Category</label>
              <input
                data-testid="opex-form-category"
                list="opex-category-options"
                className={inputCls + " w-full"}
                value={form.category}
                onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))}
                placeholder="Utilities, Premises…"
              />
              <datalist id="opex-category-options">
                {OPEX_CATEGORIES.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            {!form.variable && (
              <div><label className="overline text-ink-soft block mb-2">Period</label>
                <select data-testid="opex-form-period" className={inputCls + " w-full"} value={form.period} onChange={(e) => setForm((s) => ({ ...s, period: e.target.value }))}>
                  {PERIODS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
            )}
            {!form.variable && (
              <div className="col-span-2"><label className="overline text-ink-soft block mb-2">Amount (RM)</label>
                <input data-testid="opex-form-amount" required={!form.variable} type="number" step="0.01" className={inputCls + " w-full"} value={form.amount} onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))} />
              </div>
            )}
            {form.variable && (
              <div className="col-span-2 bg-cream/60 border border-ink/10 rounded-sm p-3 text-xs text-ink-soft">
                Amount is recorded per month after saving. Open <span className="text-matcha-dark">Monthly Bills</span> from the row to enter actuals.
              </div>
            )}
            <div><label className="overline text-ink-soft block mb-2">Active from</label>
              <input type="date" className={inputCls + " w-full"} value={form.start_date} onChange={(e) => setForm((s) => ({ ...s, start_date: e.target.value }))} />
            </div>
            <div><label className="overline text-ink-soft block mb-2">Active until (optional)</label>
              <input type="date" className={inputCls + " w-full"} value={form.end_date} onChange={(e) => setForm((s) => ({ ...s, end_date: e.target.value }))} />
            </div>
          </div>
          <div><label className="overline text-ink-soft block mb-2">Notes</label>
            <input className={inputCls + " w-full"} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} />
          </div>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mt-4">{err}</p>}
        <div className="flex justify-end gap-3 mt-8">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">Cancel</button>
          <button data-testid="opex-save" className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors">Save</button>
        </div>
      </form>
    </div>
  );
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function OpexBillsModal({ item, onClose }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [amounts, setAmounts] = useState({}); // { "YYYY-MM": "1234.50" }
  const [loading, setLoading] = useState(true);
  const [savingMonth, setSavingMonth] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/mgmt/operating-costs/${item.id}/bills`, { params: { year } });
      const map = {};
      (data || []).forEach((b) => { map[b.month] = String(b.amount); });
      setAmounts(map);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const saveMonth = async (mNum) => {
    const month = `${year}-${String(mNum + 1).padStart(2, "0")}`;
    const raw = amounts[month];
    setErr("");
    setSavingMonth(month);
    try {
      if (raw === "" || raw == null) {
        await api.delete(`/mgmt/operating-costs/${item.id}/bills/${month}`);
        setAmounts((s) => { const n = { ...s }; delete n[month]; return n; });
      } else {
        await api.put(`/mgmt/operating-costs/${item.id}/bills/${month}`, { amount: Number(raw) });
      }
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSavingMonth(null);
    }
  };

  const total = Object.values(amounts).reduce((s, v) => s + Number(v || 0), 0);

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="opex-bills-modal">
      <div className="bg-bone w-full max-w-2xl rounded-sm p-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>{item.name} · Monthly Bills</h3>
            <p className="text-xs text-ink-soft mt-1">Enter the actual amount you paid for each month. Empty months contribute 0.</p>
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <div className="flex items-center gap-3 mt-4 mb-6">
          <label className="overline text-ink-soft">Year</label>
          <input
            data-testid="opex-bills-year"
            type="number"
            min="2020"
            max="2100"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className={inputCls + " w-28"}
          />
          <p className="ml-auto text-sm text-ink-soft">
            Total {year}: <span className="text-matcha-dark">{fmt(total)}</span>
          </p>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mb-4">{err}</p>}
        {loading ? (
          <p className="text-ink-soft text-sm">Loading…</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {MONTH_LABELS.map((label, idx) => {
              const month = `${year}-${String(idx + 1).padStart(2, "0")}`;
              const val = amounts[month] ?? "";
              return (
                <div key={month} className="border border-ink/10 rounded-sm p-3" data-testid={`opex-bill-cell-${month}`}>
                  <p className="overline text-ink-soft mb-2">{label} {year}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-ink-soft">RM</span>
                    <input
                      data-testid={`opex-bill-input-${month}`}
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={val}
                      onChange={(e) => setAmounts((s) => ({ ...s, [month]: e.target.value }))}
                      onBlur={() => {
                        // Only save if value differs from saved/blank
                        saveMonth(idx);
                      }}
                      className={inputCls + " w-full text-right"}
                    />
                  </div>
                  {savingMonth === month && <p className="text-[10px] text-ink-soft mt-1">Saving…</p>}
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end mt-8">
          <button onClick={onClose} className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors" data-testid="opex-bills-close">Done</button>
        </div>
      </div>
    </div>
  );
}


function PurchasesTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [adding, setAdding] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [filter, setFilter] = useState({ ingredient_id: "", date_from: "", date_to: "" });
  const [expanded, setExpanded] = useState({}); // key -> bool
  const [selected, setSelected] = useState(new Set()); // Set<purchase.id>

  const load = async () => {
    const params = {};
    if (filter.ingredient_id) params.ingredient_id = filter.ingredient_id;
    if (filter.date_from) params.date_from = filter.date_from;
    if (filter.date_to) params.date_to = filter.date_to;
    const [purchases, ings] = await Promise.all([
      api.get("/mgmt/purchases", { params }).then((r) => r.data),
      api.get("/mgmt/ingredients").then((r) => r.data),
    ]);
    setRows(purchases);
    setIngredients(ings);
    setSelected(new Set()); // reset selection whenever the list changes
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.ingredient_id, filter.date_from, filter.date_to]);

  const del = async (p) => {
    if (!window.confirm(`Delete purchase of ${p.qty} ${ingredients.find((i) => i.id === p.ingredient_id)?.unit || ""} ${p.ingredient_name}? Stock will be reverted.`)) return;
    await api.delete(`/mgmt/ingredients/${p.ingredient_id}/purchases/${p.id}`);
    load();
  };

  // Inline P&L category tag for a single purchase. COGS = raw ingredient
  // consumed via a recipe (already counted in COGS from recipes ×
  // sales, so untouched by P&L). Packaging / Others land in the P&L
  // "Other" bucket for the month they were bought.
  const setCategory = async (p, next) => {
    if (!next || next === (p.category || "COGS")) return;
    const prev = rows;
    setRows((s) => s.map((r) => (r.id === p.id ? { ...r, category: next } : r)));
    try {
      await api.patch(`/mgmt/purchases/${p.id}`, { category: next });
    } catch (e) {
      setRows(prev);
      window.alert(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  // Bulk delete every purchase currently ticked in the table. One backend
  // call, atomic stock revert. Confirms with the count + total spent that
  // will be wiped so the admin cannot mis-click a large batch.
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const deleteSelected = async () => {
    if (selected.size === 0) return;
    const doomed = rows.filter((r) => selected.has(r.id));
    const total = doomed.reduce((s, r) => s + Number(r.total_cost || 0), 0);
    if (!window.confirm(
      `Delete ${doomed.length} purchase row${doomed.length === 1 ? "" : "s"} (RM ${total.toFixed(2)})?\n\n` +
      `Stock will be reverted for every affected ingredient. This cannot be undone.`
    )) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/mgmt/purchases/bulk-delete", { ids: Array.from(selected) });
      window.alert(`Deleted ${data.deleted} purchase row${data.deleted === 1 ? "" : "s"}.`);
      await load();
    } catch (e) {
      window.alert(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const totalSpent = rows.reduce((s, r) => s + Number(r.total_cost || 0), 0);

  // Group by (receipt_no + vendor + date). Rows without a receipt_no get a
  // unique key so they render as standalone rows. Groups with >1 line become
  // expandable — the admin can click the header to see individual items.
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.receipt_no
        ? `R::${(r.supplier || "").trim().toLowerCase()}::${r.receipt_no.trim().toUpperCase()}`
        : `X::${r.id}`; // singleton row without a receipt
      if (!map.has(key)) {
        map.set(key, {
          key,
          has_receipt: !!r.receipt_no,
          receipt_no: r.receipt_no || "",
          supplier: r.supplier || "",
          date: r.date,
          items: [],
          total: 0,
        });
      }
      const g = map.get(key);
      g.items.push(r);
      g.total += Number(r.total_cost || 0);
      // Groups collapse under whichever date is latest so grouping by
      // vendor+receipt still shows the freshest row's date.
      if (r.date > g.date) g.date = r.date;
    }
    return Array.from(map.values()).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  }, [rows]);

  const allIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleOne = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleGroup = (g) => setSelected((s) => {
    const next = new Set(s);
    const ids = g.items.map((i) => i.id);
    const alreadyAll = ids.every((id) => next.has(id));
    if (alreadyAll) { ids.forEach((id) => next.delete(id)); }
    else { ids.forEach((id) => next.add(id)); }
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));

  return (
    <div data-testid="purchases-tab">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <p className="text-ink-soft text-sm">{rows.length} purchase{rows.length === 1 ? "" : "s"} · {groups.filter((g) => g.has_receipt).length} receipt{groups.filter((g) => g.has_receipt).length === 1 ? "" : "s"}</p>
        <span className="text-ink-soft text-sm">·</span>
        <p className="text-sm">
          Total spent: <span className="text-matcha-dark">RM {totalSpent.toFixed(2)}</span>
        </p>
        {selected.size > 0 && (
          <button
            type="button"
            data-testid="purchases-bulk-delete"
            disabled={bulkDeleting}
            onClick={deleteSelected}
            className="inline-flex items-center gap-2 rounded-full border border-red-600 bg-red-600 text-bone px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-red-700 hover:border-red-700 transition-colors disabled:opacity-50"
          >
            {bulkDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            {bulkDeleting ? "Deleting…" : `Delete selected (${selected.size})`}
          </button>
        )}
        <button
          data-testid="upload-receipt-btn"
          onClick={() => setUploadingReceipt(true)}
          className="ml-auto inline-flex items-center gap-2 rounded-full px-5 py-2 border-2 border-ink text-ink text-xs uppercase tracking-[0.22em] hover:bg-ink hover:text-bone transition-colors"
        >
          <Upload size={14} /> Upload Receipt
        </button>
        <button
          data-testid="add-purchase-btn"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors"
        >
          <Plus size={14} /> Record Purchase
        </button>
      </div>

      <div className="bg-cream/40 border border-ink/10 rounded-sm p-4 mb-6">
        <p className="overline text-ink-soft mb-3">Filter</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="overline text-ink-soft block mb-1">Ingredient</label>
            <select
              data-testid="purchases-filter-ingredient"
              className={inputCls + " w-full"}
              value={filter.ingredient_id}
              onChange={(e) => setFilter((s) => ({ ...s, ingredient_id: e.target.value }))}
            >
              <option value="">All ingredients</option>
              {ingredients.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline text-ink-soft block mb-1">From</label>
            <input
              data-testid="purchases-filter-from"
              type="date"
              className={inputCls + " w-full"}
              value={filter.date_from}
              onChange={(e) => setFilter((s) => ({ ...s, date_from: e.target.value }))}
            />
          </div>
          <div>
            <label className="overline text-ink-soft block mb-1">To</label>
            <input
              data-testid="purchases-filter-to"
              type="date"
              className={inputCls + " w-full"}
              value={filter.date_to}
              onChange={(e) => setFilter((s) => ({ ...s, date_to: e.target.value }))}
            />
          </div>
          <div className="flex items-end">
            <button
              data-testid="purchases-filter-clear"
              onClick={() => setFilter({ ingredient_id: "", date_from: "", date_to: "" })}
              className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="bg-bone border border-ink/10 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-sage/40">
            <tr className="text-left text-ink-soft">
              <th className="px-4 py-3 overline w-8">
                <input
                  type="checkbox"
                  data-testid="purchases-select-all"
                  className="w-4 h-4 accent-matcha-dark"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  aria-label="Select all purchases"
                />
              </th>
              <th className="px-4 py-3 overline w-8"></th>
              <th className="px-4 py-3 overline">Date</th>
              <th className="px-4 py-3 overline">Receipt No.</th>
              <th className="px-4 py-3 overline">Item</th>
              <th className="px-4 py-3 overline">SKU</th>
              <th className="px-4 py-3 overline text-right">Qty</th>
              <th className="px-4 py-3 overline text-right">Unit Cost</th>
              <th className="px-4 py-3 overline text-right">Total</th>
              <th className="px-4 py-3 overline">Category</th>
              <th className="px-4 py-3 overline">Vendor</th>
              <th className="px-4 py-3 overline">Notes</th>
              <th className="px-4 py-3 overline">Logged By</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              // Standalone rows without a receipt render as before.
              if (!g.has_receipt) {
                const p = g.items[0];
                const ing = ingredients.find((i) => i.id === p.ingredient_id);
                return (
                  <tr key={p.id} className={`border-t border-ink/5 ${selected.has(p.id) ? "bg-red-50/40" : ""}`} data-testid={`purchase-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        data-testid={`purchase-select-${p.id}`}
                        className="w-4 h-4 accent-matcha-dark"
                        checked={selected.has(p.id)}
                        onChange={() => toggleOne(p.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 whitespace-nowrap">{p.date}</td>
                    <td className="px-4 py-3 text-ink-soft">—</td>
                    <td className="px-4 py-3">{p.ingredient_name}</td>
                    <td className="px-4 py-3 text-ink-soft whitespace-nowrap">{p.sku || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">{p.qty} {ing?.unit || ""}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">RM {Number(p.unit_cost).toFixed(4)}</td>
                    <td className="px-4 py-3 text-right text-matcha-dark whitespace-nowrap">RM {Number(p.total_cost).toFixed(2)}</td>
                    <td className="px-4 py-3"><PurchaseCategoryCell purchase={p} onChange={setCategory} /></td>
                    <td className="px-4 py-3 text-ink-soft">{p.supplier || "—"}</td>
                    <td className="px-4 py-3 text-ink-soft">{p.notes || "—"}</td>
                    <td className="px-4 py-3 text-ink-soft text-xs">{p.created_by_name || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`purchase-del-${p.id}`}
                        onClick={() => del(p)}
                        className="text-ink-soft hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              }
              // Receipt group: one header row (click to expand) + N item rows
              // when open. Header shows aggregate totals so the admin sees the
              // receipt-level number at a glance.
              const isOpen = !!expanded[g.key];
              const groupAllSelected = g.items.every((i) => selected.has(i.id));
              const groupSomeSelected = !groupAllSelected && g.items.some((i) => selected.has(i.id));
              const totalQtyLabel = g.items.length === 1
                ? `${g.items[0].qty} ${ingredients.find((i) => i.id === g.items[0].ingredient_id)?.unit || ""}`
                : `${g.items.length} lines`;
              return (
                <React.Fragment key={g.key}>
                  <tr
                    className={`border-t border-ink/5 cursor-pointer ${isOpen ? "bg-sage/10" : groupAllSelected ? "bg-red-50/40" : "hover:bg-ink/[0.02]"}`}
                    data-testid={`purchase-group-${g.key}`}
                    onClick={() => setExpanded((e) => ({ ...e, [g.key]: !e[g.key] }))}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        data-testid={`purchase-group-select-${g.key}`}
                        className="w-4 h-4 accent-matcha-dark"
                        checked={groupAllSelected}
                        ref={(el) => { if (el) el.indeterminate = groupSomeSelected; }}
                        onChange={() => toggleGroup(g)}
                      />
                    </td>
                    <td className="px-4 py-3 text-matcha-dark">{isOpen ? "▾" : "▸"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{g.date}</td>
                    <td className="px-4 py-3 font-mono text-ink">{g.receipt_no}</td>
                    <td className="px-4 py-3 text-ink-soft italic">{g.items.length} item{g.items.length === 1 ? "" : "s"} on this receipt</td>
                    <td className="px-4 py-3 text-ink-soft">—</td>
                    <td className="px-4 py-3 text-right text-ink-soft whitespace-nowrap">{totalQtyLabel}</td>
                    <td className="px-4 py-3 text-ink-soft">—</td>
                    <td className="px-4 py-3 text-right text-matcha-dark whitespace-nowrap font-medium">RM {g.total.toFixed(2)}</td>
                    <td className="px-4 py-3 text-ink-soft text-[11px] uppercase tracking-[0.18em]">
                      {(() => {
                        const cats = new Set(g.items.map((i) => i.category || "COGS"));
                        return cats.size === 1 ? [...cats][0] : "mixed";
                      })()}
                    </td>
                    <td className="px-4 py-3 text-ink-soft">{g.supplier || "—"}</td>
                    <td className="px-4 py-3 text-ink-soft" colSpan={2}>{g.items[0]?.notes || "—"}</td>
                    <td className="px-4 py-3 text-right text-[11px] uppercase tracking-[0.18em] text-ink-soft">
                      {isOpen ? "hide" : "show"}
                    </td>
                  </tr>
                  {isOpen && g.items.map((p) => {
                    const ing = ingredients.find((i) => i.id === p.ingredient_id);
                    return (
                      <tr key={p.id} className={`border-t border-ink/5 ${selected.has(p.id) ? "bg-red-50/40" : "bg-bone/60"}`} data-testid={`purchase-row-${p.id}`}>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            data-testid={`purchase-select-${p.id}`}
                            className="w-4 h-4 accent-matcha-dark"
                            checked={selected.has(p.id)}
                            onChange={() => toggleOne(p.id)}
                          />
                        </td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-ink-soft whitespace-nowrap pl-8">{p.date}</td>
                        <td className="px-4 py-3 font-mono text-ink-soft text-xs">↳ {p.receipt_no}</td>
                        <td className="px-4 py-3 text-ink">{p.ingredient_name}</td>
                        <td className="px-4 py-3 text-ink-soft whitespace-nowrap">{p.sku || "—"}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">{p.qty} {ing?.unit || ""}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">RM {Number(p.unit_cost).toFixed(4)}</td>
                        <td className="px-4 py-3 text-right text-matcha-dark whitespace-nowrap">RM {Number(p.total_cost).toFixed(2)}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><PurchaseCategoryCell purchase={p} onChange={setCategory} /></td>
                        <td className="px-4 py-3 text-ink-soft">{p.supplier || "—"}</td>
                        <td className="px-4 py-3 text-ink-soft">{p.notes || "—"}</td>
                        <td className="px-4 py-3 text-ink-soft text-xs">{p.created_by_name || "—"}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            data-testid={`purchase-del-${p.id}`}
                            onClick={(e) => { e.stopPropagation(); del(p); }}
                            className="text-ink-soft hover:text-red-600"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={13} className="p-8 text-center text-ink-soft">No purchases recorded. Click <span className="text-matcha-dark">+ Record Purchase</span> to add one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {adding && (
        <RecordPurchaseModal
          ingredients={ingredients}
          defaultDate={today}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); load(); }}
        />
      )}
      {uploadingReceipt && (
        <ReceiptUploadModal
          defaultDate={today}
          onClose={() => setUploadingReceipt(false)}
          onSaved={() => { setUploadingReceipt(false); load(); }}
        />
      )}
    </div>
  );
}

function RecordPurchaseModal({ ingredients: _legacy, defaultDate, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(defaultDate);
  const [filter, setFilter] = useState("frequent"); // frequent | all | category key
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({}); // {id: {qty}}
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/mgmt/ingredients-with-stats");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const set = new Set();
    items.forEach((i) => { if (i.category) set.add(i.category); });
    return Array.from(set).sort();
  }, [items]);

  const visible = useMemo(() => {
    let rows = items;
    if (filter === "frequent") {
      rows = rows.filter((i) => (i.recent_purchase_count || 0) >= 2);
    } else if (filter !== "all") {
      rows = rows.filter((i) => i.category === filter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((i) => i.name.toLowerCase().includes(q));
    }
    return rows;
  }, [items, filter, search]);

  const selectedIds = Object.keys(selected);

  const toggle = (ing) => {
    setSelected((s) => {
      const next = { ...s };
      if (next[ing.id]) {
        delete next[ing.id];
      } else {
        const last = ing.last_purchase || {};
        // Pre-fill unit_cost by cross-referencing: last-paid price wins,
        // ingredient's current cost_per_unit is the fallback. Admin can
        // still override before saving. Saves tons of typing when the
        // same supplier is bought repeatedly.
        const prefilledCost = (last.unit_cost != null && last.unit_cost >= 0)
          ? String(last.unit_cost)
          : (ing.cost_per_unit != null && ing.cost_per_unit > 0 ? String(ing.cost_per_unit) : "");
        next[ing.id] = {
          qty: last.qty ? String(last.qty) : "",
          unit_cost: prefilledCost,
        };
      }
      return next;
    });
  };

  const patchSelected = (id, patch) =>
    setSelected((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const lines = selectedIds
      .map((id) => ({
        ingredient_id: id,
        qty: Number(selected[id].qty),
        unit_cost: Number(selected[id].unit_cost || 0),
      }))
      .filter((l) => l.qty > 0);
    if (lines.length === 0) { setErr("Tick at least one item and enter a quantity."); return; }
    setBusy(true);
    try {
      await api.post("/inventory/purchases", { date, items: lines });
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const filterTabs = [
    { k: "frequent", label: "⭐ Frequent" },
    { k: "all", label: "All Items" },
    ...categories.map((c) => ({ k: c, label: c })),
  ];

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="record-purchase-modal">
      <form onSubmit={submit} className="bg-bone w-full max-w-4xl rounded-sm p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>Record Purchase</h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>

        <div className="mb-6">
          <label className="overline text-ink-soft block mb-2">Date stock in</label>
          <input data-testid="rp-date" required type="date" className={inputCls + " w-full sm:max-w-xs"} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>

        <div className="flex flex-wrap gap-2 mb-3" data-testid="rp-filter-tabs">
          {filterTabs.map((t) => (
            <button
              type="button"
              key={t.k}
              data-testid={`rp-filter-${t.k}`}
              onClick={() => setFilter(t.k)}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border transition-colors ${
                filter === t.k
                  ? "bg-ink text-bone border-ink"
                  : "bg-bone text-ink-soft border-ink/15 hover:border-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <input
          data-testid="rp-search"
          type="search"
          placeholder="Search items…"
          className={inputCls + " w-full mb-4"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="bg-cream/40 border border-ink/10 rounded-sm max-h-[40vh] overflow-y-auto mb-6">
          {visible.length === 0 && (
            <p className="p-6 text-center text-ink-soft text-sm">
              {filter === "frequent" ? "No frequently-purchased items yet. Switch to All Items." : "No items match."}
            </p>
          )}
          <ul className="divide-y divide-ink/5">
            {visible.map((i) => {
              const isSelected = !!selected[i.id];
              const cell = selected[i.id] || {};
              const last = i.last_purchase;
              return (
                <li key={i.id} data-testid={`rp-item-${i.id}`} className={`px-4 py-3 ${isSelected ? "bg-sage/20" : ""}`}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`rp-check-${i.id}`}
                      checked={isSelected}
                      onChange={() => toggle(i)}
                      className="w-4 h-4 accent-matcha-dark"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-ink text-sm">{i.name}</span>
                        {i.code && <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink bg-sage/40 px-1.5 py-0.5 rounded-sm">{i.code}</span>}
                        <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">({i.unit})</span>
                        {(i.recent_purchase_count || 0) >= 2 && (
                          <span className="text-[10px] uppercase tracking-[0.18em] text-matcha-dark">⭐ frequent</span>
                        )}
                        {i.category && <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft/70">· {i.category}</span>}
                      </div>
                      <p className="text-[10px] text-ink-soft mt-0.5">
                        Stock: {i.stock_qty} {i.unit}
                        {last && <>
                          {" · "}Last bought {last.date}: {last.qty} {i.unit}
                          {last.unit_cost != null && <> @ RM {Number(last.unit_cost).toFixed(4)}/{i.unit}</>}
                        </>}
                        {!last && i.cost_per_unit > 0 && <> · Catalog price RM {Number(i.cost_per_unit).toFixed(4)}/{i.unit}</>}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap" data-testid={`rp-fields-${i.id}`} onClick={(e) => e.preventDefault()}>
                        <input
                          data-testid={`rp-qty-${i.id}`}
                          type="number" step="0.001" min="0" required
                          placeholder="Qty"
                          className={inputCls + " w-24 text-right"}
                          value={cell.qty}
                          onChange={(e) => patchSelected(i.id, { qty: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft w-8">{i.unit}</span>
                        <span className="text-ink-soft text-xs">@</span>
                        {/* Unit cost — pre-filled from last purchase (or
                            catalog price) so admin usually just confirms;
                            editable so one-off price changes are easy. */}
                        <input
                          data-testid={`rp-cost-${i.id}`}
                          type="number" step="0.0001" min="0"
                          placeholder="Price"
                          className={inputCls + " w-28 text-right tabular-nums"}
                          value={cell.unit_cost ?? ""}
                          onChange={(e) => patchSelected(i.id, { unit_cost: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                          title="RM per unit — pre-filled from last purchase price. Edit if the vendor changed it."
                        />
                        <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft w-10">RM/{i.unit}</span>
                        {Number(cell.qty) > 0 && Number(cell.unit_cost) > 0 && (
                          <span className="text-xs text-matcha-dark tabular-nums whitespace-nowrap" data-testid={`rp-line-total-${i.id}`}>
                            = RM {(Number(cell.qty) * Number(cell.unit_cost)).toFixed(2)}
                          </span>
                        )}
                      </div>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <p className="text-sm text-ink-soft" data-testid="rp-selected-summary">
            <span className="text-matcha-dark">{selectedIds.length}</span> item{selectedIds.length === 1 ? "" : "s"} selected
            {selectedIds.length > 0 && (() => {
              const total = selectedIds.reduce((acc, id) => {
                const c = selected[id] || {};
                return acc + (Number(c.qty) || 0) * (Number(c.unit_cost) || 0);
              }, 0);
              return total > 0 ? (
                <span className="ml-2 text-ink" data-testid="rp-total">
                  · Total <strong className="text-matcha-dark">RM {total.toFixed(2)}</strong>
                </span>
              ) : null;
            })()}
          </p>
          {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-1.5 rounded-sm">{err}</p>}
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">Cancel</button>
          <button
            data-testid="rp-submit"
            disabled={busy || selectedIds.length === 0}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function BatchesTab() {
  const thisMonth = () => new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(thisMonth());
  const [scope, setScope] = useState("month"); // "month" | "all"
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = scope === "month" ? { month } : {};
      const { data } = await api.get("/mgmt/batches", { params });
      setRows(data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, scope]);

  const visible = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) =>
      (r.batch_sku || "").toLowerCase().includes(q) ||
      (r.ingredient_name || "").toLowerCase().includes(q) ||
      (r.code || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totalBatches = visible.length;
  const totalPurchases = visible.reduce((s, r) => s + Number(r.purchases_count || 0), 0);
  const totalQty = visible.reduce((s, r) => s + Number(r.total_qty || 0), 0);

  return (
    <div data-testid="batches-tab">
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <div className="inline-flex rounded-full bg-cream/40 border border-ink/10 p-1" data-testid="batches-scope">
          <button
            type="button"
            data-testid="batches-scope-month"
            onClick={() => setScope("month")}
            className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] transition-colors ${scope === "month" ? "bg-ink text-bone" : "text-ink-soft"}`}
          >This month</button>
          <button
            type="button"
            data-testid="batches-scope-all"
            onClick={() => setScope("all")}
            className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] transition-colors ${scope === "all" ? "bg-ink text-bone" : "text-ink-soft"}`}
          >All-time</button>
        </div>
        {scope === "month" && (
          <input
            data-testid="batches-month"
            type="month"
            className={inputCls}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        )}
        <input
          data-testid="batches-search"
          type="search"
          placeholder="Search SKU, item, code…"
          className={inputCls + " flex-1 min-w-[220px]"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="ml-auto flex items-center gap-6 flex-wrap">
          <span className="overline text-ink-soft">
            Batches: <span className="text-ink text-base ml-1" data-testid="batches-total">{totalBatches}</span>
          </span>
          <span className="overline text-ink-soft">
            Purchases: <span className="text-matcha-dark text-base ml-1" data-testid="batches-purchases">{totalPurchases}</span>
          </span>
          <span className="overline text-ink-soft">
            Total qty in: <span className="text-matcha-dark text-base ml-1" data-testid="batches-qty">{Number(totalQty || 0).toFixed(3).replace(/\.?0+$/, "")}</span>
          </span>
        </div>
      </div>

      <div className="overflow-x-auto border border-ink/10 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-cream/60 text-left text-ink-soft">
            <tr>
              <th className="px-4 py-3 overline">Batch SKU</th>
              <th className="px-4 py-3 overline">Item</th>
              <th className="px-4 py-3 overline">Code</th>
              <th className="px-4 py-3 overline text-right">Times bought</th>
              <th className="px-4 py-3 overline text-right">Qty in</th>
              <th className="px-4 py-3 overline text-right">Item stock now</th>
              <th className="px-4 py-3 overline">Last purchase</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.batch_sku} data-testid={`batch-row-${r.batch_sku}`} className="border-t border-ink/5">
                <td className="px-4 py-3 font-mono text-[11px] text-ink">{r.batch_sku}</td>
                <td className="px-4 py-3 text-ink">{r.ingredient_name || "—"}</td>
                <td className="px-4 py-3">
                  {r.code && <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink bg-sage/40 px-1.5 py-0.5 rounded-sm">{r.code}</span>}
                </td>
                <td className="px-4 py-3 text-right font-mono">{r.purchases_count}</td>
                <td className="px-4 py-3 text-right font-mono">
                  {Number(r.total_qty || 0).toFixed(3).replace(/\.?0+$/, "")} <span className="text-ink-soft">{r.unit}</span>
                </td>
                <td className="px-4 py-3 text-right font-mono text-matcha-dark">
                  {Number(r.current_stock || 0).toFixed(3).replace(/\.?0+$/, "")} <span className="text-ink-soft">{r.unit}</span>
                </td>
                <td className="px-4 py-3 text-ink-soft text-xs">{r.last_date}</td>
              </tr>
            ))}
            {!busy && visible.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-ink-soft">No batches for this {scope === "month" ? "month" : "range"}.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-ink-soft mt-3">
        &quot;Item stock now&quot; is the ingredient&apos;s <em>total</em> remaining stock (aggregated across all batches); per-batch remaining requires FIFO tracking (roadmap).
      </p>
    </div>
  );
}

function ReceiptUploadModal({ defaultDate, onClose, onSaved }) {
  // Flow: PICK → UPLOADING → REVIEW → COMMITTING
  // Supports dropping / picking multiple receipts at once — every file is
  // parsed in parallel, all line items are merged into one review table so
  // the admin can commit a whole delivery's worth of receipts in a single go.
  // Each file always commits as its OWN receipt (own vendor / date / invoice
  // no.) — never merged, since two receipts with different invoice numbers
  // must stay independent purchase groups in the Purchases table.
  const [phase, setPhase] = useState("pick");
  const [files, setFiles] = useState([]);         // File[]
  const [dragOver, setDragOver] = useState(false);
  const [err, setErr] = useState("");
  // parsedList: one entry per file with editable meta (vendor / date / receipt_no)
  // + status flags for commit / duplicate.
  const [parsedList, setParsedList] = useState([]);
  const [ingredientsCatalog, setIngredientsCatalog] = useState([]);
  const [rows, setRows] = useState([]);       // merged editable copy of parsed items, each row keeps `source_file`
  const [notes, setNotes] = useState("");
  const patchFile = (filename, patch) =>
    setParsedList((s) => s.map((p) => (p.filename === filename ? { ...p, ...patch } : p)));

  const validateFile = (f) => {
    if (!f) return "Missing file.";
    const ok = /\.(pdf|jpe?g|png|webp)$/i.test(f.name) ||
      ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"].includes((f.type || "").toLowerCase());
    if (!ok) return `${f.name}: unsupported type (JPEG/PNG/WEBP/PDF only).`;
    if (f.size > 12 * 1024 * 1024) return `${f.name}: file exceeds 12 MB.`;
    return null;
  };

  const addFiles = (list) => {
    setErr("");
    const incoming = Array.from(list || []);
    const errors = [];
    const good = [];
    for (const f of incoming) {
      const e = validateFile(f);
      if (e) errors.push(e);
      else good.push(f);
    }
    if (errors.length) setErr(errors.join(" "));
    if (!good.length) return;
    // De-dupe by name+size so double-adds don't create duplicates.
    setFiles((prev) => {
      const key = (f) => `${f.name}::${f.size}`;
      const seen = new Set(prev.map(key));
      return [...prev, ...good.filter((f) => !seen.has(key(f)))];
    });
  };

  const removeFile = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const runOCR = async () => {
    if (!files.length) return;
    setErr("");
    setPhase("uploading");
    const results = await Promise.all(files.map(async (f) => {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/mgmt/receipts/parse", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        return { filename: f.name, ok: true, data };
      } catch (e) {
        return { filename: f.name, ok: false, error: formatErr(e?.response?.data?.detail) || e.message };
      }
    }));

    const successes = results.filter((r) => r.ok);
    const failures = results.filter((r) => !r.ok);
    if (!successes.length) {
      setErr(`Every file failed to parse: ${failures.map((f) => `${f.filename} — ${f.error}`).join("; ")}`);
      setPhase("pick");
      return;
    }

    // All receipts share the ingredients_catalog, so grab it from the first
    // successful parse. No shared vendor/date/receipt_no — each file keeps
    // its own so nothing gets merged.
    const first = successes[0].data;
    setIngredientsCatalog(first.ingredients_catalog || []);

    const merged = [];
    const summary = [];
    for (const r of results) {
      if (!r.ok) {
        summary.push({ filename: r.filename, error: r.error, items_count: 0 });
        continue;
      }
      const items = (r.data.items || []).map((x) => ({
        source_file: r.filename,
        extracted_name: x.extracted_name || "",
        match_ingredient_id: x.match_ingredient_id || "",
        match_name: x.match_name || "",
        match_unit: x.match_unit || "",
        qty: x.qty || 0,
        unit_price: x.unit_price || 0,
        line_total: x.line_total || 0,
        accepted: !!x.accepted,
        // Kind (tab) the auto-create should land under. Default is
        // "physical" but admin overrides per line via a dropdown before
        // commit. Ignored when the row already maps to an existing
        // ingredient.
        new_kind: "physical",
      }));
      merged.push(...items);
      const parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(r.data.date || "") ? r.data.date : defaultDate;
      summary.push({
        filename: r.filename,
        vendor: r.data.vendor || "",
        date: parsedDate,
        currency: r.data.currency || "RM",
        grand_total: r.data.grand_total || 0,
        receipt_no: r.data.receipt_no || "",
        items_count: items.length,
        // status flags used by commit-all to render success/duplicate/error state per file
        status: "pending", // pending | committed | duplicate | error
        message: "",
      });
    }
    setRows(merged);
    setParsedList(summary);
    if (failures.length) {
      setErr(`Some files failed: ${failures.map((f) => `${f.filename} (${f.error})`).join("; ")}`);
    }
    setPhase("review");
  };

  const patchRow = (i, patch) =>
    setRows((s) => s.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const toggle = (i) => patchRow(i, { accepted: !rows[i].accepted });

  // Commit each file as its OWN receipt. One POST per parsed file, using
  // that file's own vendor / date / invoice number. If any single file trips
  // the duplicate guard, we mark just that file and skip its commit — the
  // rest still land. `forceFilenames` re-tries the flagged files with
  // `force: true` (used by the per-file "Import anyway" action).
  const commit = async (forceFilenames = null) => {
    setErr("");
    // Validate: every kept file needs at least one accepted row and a valid date.
    const targets = parsedList.filter((p) => !p.error && p.status !== "committed");
    if (targets.length === 0) {
      setErr("Nothing left to import — all files were already committed or failed.");
      return;
    }
    for (const p of targets) {
      // Accept rows that either map to a catalog ingredient OR have an
      // extracted_name we can auto-create from — the create step below
      // handles the mapping so the admin no longer has to click "+ New".
      const fileRows = rows.filter((r) =>
        r.source_file === p.filename && r.accepted && Number(r.qty) > 0
        && (r.match_ingredient_id || (r.extracted_name || "").trim())
      );
      if (!/^\d{4}-\d{2}-\d{2}$/.test(p.date || "")) {
        setErr(`Pick a valid date for ${p.filename}.`);
        return;
      }
      if (fileRows.length === 0) {
        setErr(`${p.filename}: tick at least one row with a name and a quantity.`);
        return;
      }
    }
    setPhase("committing");

    // Auto-create unmapped rows as fresh ingredients so the admin can import
    // a receipt with brand-new items without leaving the modal. Uses the
    // receipt's unit_price as the initial cost_per_unit and falls back to
    // "piece" for the unit when the row didn't carry one (admin can rename
    // + change unit later on the Ingredients tab). Newly-created rows are
    // patched into `rows` + `ingredientsCatalog` so subsequent duplicate
    // extracted names in the same session reuse the same fresh ingredient.
    const nameKey = (s) => (s || "").trim().toLowerCase();
    const catalogByName = new Map();
    for (const ing of ingredientsCatalog) catalogByName.set(nameKey(ing.name), ing);
    const sessionNew = new Map(); // extracted_name(lower) -> new ingredient
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      if (!r.accepted) continue;
      if (r.match_ingredient_id) continue;
      const name = (r.extracted_name || "").trim();
      if (!name) continue;
      // Reuse if the same extracted name was auto-created earlier in this
      // save pass, or already lives in the catalog under the exact name.
      const key = nameKey(name);
      let ing = sessionNew.get(key) || catalogByName.get(key);
      if (!ing) {
        try {
          const { data } = await api.post("/mgmt/ingredients", {
            name,
            unit: r.match_unit || "piece",
            cost_per_unit: Number(r.unit_price || 0),
            // Admin-picked tab (physical / component / ingredient /
            // matcha / cups / packaging). Falls back to physical if
            // the row was never touched.
            kind: r.new_kind || "physical",
          });
          ing = data;
          sessionNew.set(key, ing);
        } catch (e) {
          setPhase("review");
          setErr(`Could not auto-create "${name}": ${formatErr(e?.response?.data?.detail) || e.message}`);
          return;
        }
      }
      // Patch this row so the POST loop below uses the fresh id.
      rows[i] = {
        ...r,
        match_ingredient_id: ing.id,
        match_name: ing.name,
        match_unit: ing.unit,
      };
    }
    if (sessionNew.size > 0) {
      const created = Array.from(sessionNew.values());
      setIngredientsCatalog((s) => [...s, ...created]);
      setRows([...rows]);
    }

    const shouldForce = (fn) => Array.isArray(forceFilenames) && forceFilenames.includes(fn);
    let succeeded = 0;
    let duplicates = 0;
    let failures = 0;
    for (const p of targets) {
      const fileRows = rows
        .filter((r) => r.source_file === p.filename && r.accepted && r.match_ingredient_id && Number(r.qty) > 0)
        .map((r) => ({
          ingredient_id: r.match_ingredient_id,
          qty: Number(r.qty),
          unit_price: Number(r.unit_price || 0),
        }));
      try {
        await api.post("/mgmt/receipts/commit", {
          date: p.date,
          vendor: p.vendor,
          receipt_no: p.receipt_no,
          items: fileRows,
          notes,
          force: shouldForce(p.filename),
        });
        patchFile(p.filename, { status: "committed", message: "" });
        succeeded += 1;
      } catch (e) {
        const status = e?.response?.status;
        const msg = formatErr(e?.response?.data?.detail) || e.message;
        if (status === 409) {
          patchFile(p.filename, { status: "duplicate", message: msg });
          duplicates += 1;
        } else {
          patchFile(p.filename, { status: "error", message: msg });
          failures += 1;
        }
      }
    }
    // Always close the modal when at least one file committed and there are
    // no duplicates/failures left to resolve — the user asked for a clean
    // "save & exit" so they can immediately upload another receipt.
    if (duplicates === 0 && failures === 0 && succeeded > 0) {
      onSaved();
      return;
    }
    setPhase("review");
    if (duplicates + failures > 0) {
      setErr(
        `Imported ${succeeded} of ${targets.length} receipts.` +
        (duplicates ? ` ${duplicates} duplicate${duplicates === 1 ? "" : "s"} — click "Import anyway" on the file to override.` : "") +
        (failures ? ` ${failures} failed — see per-file message.` : "")
      );
    }
  };

  // A row counts as "ready" when it's ticked, has a positive qty, and
  // EITHER maps to an existing ingredient OR carries an extracted_name we
  // can auto-create on save. Keeping the definition here means the "Save"
  // button lights up as soon as OCR gives us a name — the admin no longer
  // has to hand-map every new item.
  const isReadyRow = (r) =>
    r.accepted && Number(r.qty || 0) > 0
    && (r.match_ingredient_id || (r.extracted_name || "").trim());
  const acceptedCount = rows.filter(isReadyRow).length;
  const acceptedTotal = rows
    .filter(isReadyRow)
    .reduce((s, r) => s + Number(r.qty || 0) * Number(r.unit_price || 0), 0);

  // Files that still need committing (not yet successfully imported and not
  // parse-failed). Drives both the Save button label and its disabled state.
  const pendingFiles = parsedList.filter((p) => !p.error && p.status !== "committed").length;
  const multiFile = files.length > 1 || parsedList.length > 1;

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="receipt-upload-modal">
      <div className="bg-bone w-full max-w-5xl rounded-sm p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>Upload Receipts</h3>
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-1">
              AI-powered OCR · drop one or many · JPEG / PNG / PDF · max 12 MB each
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="receipt-close" className="text-ink-soft hover:text-ink">
            <X size={20} />
          </button>
        </div>

        {/* PICK phase */}
        {phase === "pick" && (
          <div>
            <label
              htmlFor="receipt-file-input"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer?.files);
              }}
              className={`block border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-colors ${dragOver ? "border-matcha-dark bg-sage/15" : "border-ink/25 hover:border-matcha-dark"}`}
              data-testid="receipt-dropzone"
            >
              <FileText size={32} className="mx-auto mb-3 text-ink-soft" />
              <p className="text-ink text-sm mb-1">
                {files.length
                  ? `${files.length} receipt${files.length === 1 ? "" : "s"} ready · click or drop more`
                  : "Drop your receipts here or click to browse"}
              </p>
              <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em]">
                JPEG · PNG · WEBP · PDF · 12 MB each
              </p>
              <input
                id="receipt-file-input"
                data-testid="receipt-file-input"
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf"
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              />
            </label>

            {/* Selected file list — lets the admin remove individual files
                before running OCR so a mis-drop doesn't need a full restart. */}
            {files.length > 0 && (
              <ul className="mt-4 space-y-1.5 text-sm" data-testid="receipt-file-list">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-3 bg-bone border border-ink/10 rounded-sm px-3 py-2"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <FileText size={14} className="text-matcha-dark shrink-0" />
                      <span className="text-ink truncate">{f.name}</span>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft shrink-0">
                        {(f.size / 1024).toFixed(0)} KB
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      data-testid={`receipt-file-remove-${i}`}
                      className="text-ink-soft hover:text-red-700"
                      title="Remove this file"
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mt-4">{err}</p>}
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="receipt-parse-btn"
                disabled={!files.length}
                onClick={runOCR}
                className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50"
              >
                <Upload size={14} /> Scan {files.length || ""} Receipt{files.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        )}

        {/* UPLOADING phase */}
        {phase === "uploading" && (
          <div className="py-16 text-center" data-testid="receipt-uploading">
            <Loader2 size={36} className="mx-auto mb-4 text-matcha-dark animate-spin" />
            <p className="text-ink text-sm">
              Scanning {files.length} receipt{files.length === 1 ? "" : "s"} with AI…
            </p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-2">
              This usually takes 10–20 seconds per file
            </p>
          </div>
        )}

        {/* REVIEW phase */}
        {phase === "review" && parsedList.length > 0 && (
          <div data-testid="receipt-review">
            {/* One editable card per file. Each file will commit as its OWN
                receipt so different invoice numbers stay independent. Failed
                or duplicate files show inline messages here. */}
            <div className="space-y-3 mb-6" data-testid="receipt-file-cards">
              {parsedList.map((p) => {
                const statusColour =
                  p.error ? "border-red-300 bg-red-50" :
                  p.status === "committed" ? "border-matcha-dark/40 bg-sage/15" :
                  p.status === "duplicate" ? "border-amber-400 bg-amber-50" :
                  p.status === "error" ? "border-red-300 bg-red-50" :
                  "border-ink/10 bg-bone";
                return (
                  <div
                    key={p.filename}
                    className={`rounded-sm border p-4 ${statusColour}`}
                    data-testid={`receipt-file-card-${p.filename}`}
                  >
                    <div className="flex items-start gap-3 flex-wrap">
                      <FileText size={16} className="text-matcha-dark mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-ink text-sm font-medium truncate">{p.filename}</span>
                          <span className="text-[11px] uppercase tracking-[0.18em] text-ink-soft">
                            {p.error ? "parse failed" : `${p.items_count} rows · ${p.currency || "RM"} ${Number(p.grand_total || 0).toFixed(2)}`}
                          </span>
                          {p.status === "committed" && (
                            <span className="text-[11px] uppercase tracking-[0.22em] text-matcha-dark">✓ Imported</span>
                          )}
                          {p.status === "duplicate" && (
                            <span className="text-[11px] uppercase tracking-[0.22em] text-amber-700">Duplicate</span>
                          )}
                        </div>
                        {(p.error || p.message) && (
                          <p className="text-[12px] text-red-700 mt-1">
                            {p.error || p.message}
                          </p>
                        )}
                        {!p.error && p.status !== "committed" && (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                            <div>
                              <label className="overline text-ink-soft block mb-1">Vendor</label>
                              <input
                                data-testid={`receipt-vendor-${p.filename}`}
                                className={inputCls + " w-full"}
                                value={p.vendor}
                                onChange={(e) => patchFile(p.filename, { vendor: e.target.value, status: "pending", message: "" })}
                                placeholder="Supplier name"
                              />
                            </div>
                            <div>
                              <label className="overline text-ink-soft block mb-1">Receipt No.</label>
                              <input
                                data-testid={`receipt-no-${p.filename}`}
                                className={inputCls + " w-full font-mono"}
                                value={p.receipt_no}
                                onChange={(e) => patchFile(p.filename, { receipt_no: e.target.value, status: "pending", message: "" })}
                                placeholder="INV-123 · optional"
                              />
                            </div>
                            <div>
                              <label className="overline text-ink-soft block mb-1">Purchase date</label>
                              <input
                                data-testid={`receipt-date-${p.filename}`}
                                type="date"
                                className={inputCls + " w-full"}
                                value={p.date}
                                onChange={(e) => patchFile(p.filename, { date: e.target.value, status: "pending", message: "" })}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      {p.status === "duplicate" && (
                        <button
                          type="button"
                          data-testid={`receipt-force-commit-${p.filename}`}
                          onClick={() => commit([p.filename])}
                          className="whitespace-nowrap text-[11px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border border-amber-600 text-amber-800 hover:bg-amber-600 hover:text-bone transition-colors"
                        >
                          Import anyway
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Shared notes — one line typed once, applied to every receipt in
                this batch. Rarely edited but useful for a bulk delivery note. */}
            <div className="mb-6">
              <label className="overline text-ink-soft block mb-1">Notes (shared across all receipts)</label>
              <input
                data-testid="receipt-notes"
                className={inputCls + " w-full"}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional — e.g. Weekly delivery from City Grocer"
              />
            </div>

            <div className="border border-ink/10 rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-sage/40 text-left text-ink-soft">
                  <tr>
                    <th className="px-3 py-2 overline w-10"></th>
                    <th className="px-3 py-2 overline">Item on receipt</th>
                    {multiFile && <th className="px-3 py-2 overline">Source file</th>}
                    <th className="px-3 py-2 overline">Map to ingredient</th>
                    <th className="px-3 py-2 overline text-right">Qty</th>
                    <th className="px-3 py-2 overline text-right">Unit price</th>
                    <th className="px-3 py-2 overline text-right">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr
                      key={i}
                      data-testid={`receipt-row-${i}`}
                      className={`border-t border-ink/5 ${r.accepted ? "bg-sage/10" : ""}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          data-testid={`receipt-row-check-${i}`}
                          className="w-4 h-4 accent-matcha-dark"
                          checked={r.accepted}
                          onChange={() => toggle(i)}
                        />
                      </td>
                      <td className="px-3 py-2 text-ink">{r.extracted_name || "—"}</td>
                      {multiFile && (
                        <td className="px-3 py-2 text-[11px] uppercase tracking-[0.18em] text-ink-soft truncate max-w-[160px]" title={r.source_file}>
                          {r.source_file}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <IngredientPickerCell
                          value={r.match_ingredient_id}
                          onChange={(patch) => patchRow(i, patch)}
                          ingredientsCatalog={ingredientsCatalog}
                          onCatalogAdd={(ing) => setIngredientsCatalog((s) => [...s, ing])}
                          extractedName={r.extracted_name}
                          unitHint="ml"
                          testidPrefix={`receipt-ing-${i}`}
                        />
                        {!r.match_ingredient_id && (r.extracted_name || "").trim() && (
                          <select
                            data-testid={`receipt-row-kind-${i}`}
                            value={r.new_kind || "physical"}
                            onChange={(e) => patchRow(i, { new_kind: e.target.value })}
                            title="Which Ingredients tab this new SKU lands in"
                            className="mt-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 focus:outline-none focus:ring-1 focus:ring-matcha-dark cursor-pointer"
                          >
                            <option value="physical">→ Physical Items</option>
                            <option value="component">→ Daily Components</option>
                            <option value="ingredient">→ Daily Ingredients</option>
                            <option value="matcha">→ Matcha &amp; Hojicha Powder</option>
                            <option value="cups">→ Cups</option>
                            <option value="packaging">→ Packaging</option>
                          </select>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <input
                          data-testid={`receipt-row-qty-${i}`}
                          type="number" step="0.001" min="0"
                          className={inputCls + " w-24 text-right"}
                          value={r.qty}
                          onChange={(e) => patchRow(i, { qty: e.target.value })}
                        />
                        <span className="ml-1 text-[10px] uppercase tracking-[0.18em] text-ink-soft">
                          {r.match_unit || ""}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <input
                          data-testid={`receipt-row-price-${i}`}
                          type="number" step="0.01" min="0"
                          className={inputCls + " w-24 text-right"}
                          value={r.unit_price}
                          onChange={(e) => patchRow(i, { unit_price: e.target.value })}
                        />
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-ink-soft whitespace-nowrap">
                        {(Number(r.qty || 0) * Number(r.unit_price || 0)).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={multiFile ? 7 : 6} className="p-6 text-center text-ink-soft">No line items were detected.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap mt-4">
              <p className="text-sm text-ink-soft" data-testid="receipt-summary">
                <span className="text-matcha-dark">{acceptedCount}</span> item{acceptedCount === 1 ? "" : "s"} ready ·
                <span className="text-ink ml-1">RM {acceptedTotal.toFixed(2)}</span>
              </p>
              {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-1.5 rounded-sm">{err}</p>}
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                data-testid="receipt-back"
                onClick={() => { setPhase("pick"); setParsedList([]); setRows([]); setFiles([]); setNotes(""); }}
                className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink"
              >
                ← Rescan
              </button>
              <button
                type="button"
                data-testid="receipt-commit-btn"
                disabled={acceptedCount === 0 || pendingFiles === 0}
                onClick={() => commit(null)}
                className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50"
              >
                <CheckIcon size={14} /> Save {pendingFiles || ""} receipt{pendingFiles === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        )}

        {/* COMMITTING phase */}
        {phase === "committing" && (
          <div className="py-16 text-center" data-testid="receipt-committing">
            <Loader2 size={36} className="mx-auto mb-4 text-matcha-dark animate-spin" />
            <p className="text-ink text-sm">Saving purchases…</p>
          </div>
        )}
      </div>
    </div>
  );
}


// -------------------------------------------------------------------
// RecipeBuilderModal — drag & drop 30-40 recipe card images, batch OCR,
// review + auto-match ingredients & product, commit as product recipes.
// Every recipe = one product; sizes ("12oz" / "16oz") stay separate.
// Editable qty anytime by re-scanning or via the Product editor.
// -------------------------------------------------------------------
function RecipeBuilderModal({ onClose, onSaved }) {
  const [phase, setPhase] = useState("pick"); // pick | uploading | review | committing
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [err, setErr] = useState("");
  // Per-file parsed state — filename → { drink_name, cup_size_oz, full_name, items[], ...status }
  const [parsedFiles, setParsedFiles] = useState([]);
  const [ingredientsCatalog, setIngredientsCatalog] = useState([]);
  const [productsCatalog, setProductsCatalog] = useState([]);

  const validate = (f) => {
    if (!/\.(pdf|jpe?g|png|webp)$/i.test(f.name)) return `${f.name}: unsupported type`;
    if (f.size > 12 * 1024 * 1024) return `${f.name}: exceeds 12 MB`;
    return null;
  };
  const addFiles = (list) => {
    setErr("");
    const errors = []; const good = [];
    for (const f of Array.from(list || [])) {
      const e = validate(f); if (e) errors.push(e); else good.push(f);
    }
    if (errors.length) setErr(errors.join(" "));
    setFiles((prev) => {
      const key = (f) => `${f.name}::${f.size}`;
      const seen = new Set(prev.map(key));
      return [...prev, ...good.filter((f) => !seen.has(key(f)))];
    });
  };
  const removeFile = (i) => setFiles((s) => s.filter((_, idx) => idx !== i));

  const runOCR = async () => {
    if (!files.length) return;
    setErr(""); setPhase("uploading");
    // Read each file to a data-URL in parallel so we can both send it to the
    // parser and later store it as the product's thumbnail on commit.
    const readAsDataURL = (f) => new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(f);
    });
    const results = await Promise.all(files.map(async (f) => {
      try {
        const [dataUrl, resp] = await Promise.all([
          readAsDataURL(f),
          (async () => {
            const fd = new FormData();
            fd.append("file", f);
            return api.post("/mgmt/recipes/parse", fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          })(),
        ]);
        return { filename: f.name, ok: true, data: resp.data, thumbnail_b64: dataUrl };
      } catch (e) {
        return { filename: f.name, ok: false, error: formatErr(e?.response?.data?.detail) || e.message };
      }
    }));
    const successes = results.filter((r) => r.ok);
    if (!successes.length) {
      setErr(`Every file failed: ${results.map((r) => `${r.filename} — ${r.error}`).join("; ")}`);
      setPhase("pick");
      return;
    }
    // All parses share the same ingredients/products catalog — grab from first.
    const first = successes[0].data;
    setIngredientsCatalog(first.ingredients_catalog || []);
    setProductsCatalog(first.products_catalog || []);
    const cards = results.map((r) => {
      if (!r.ok) return { filename: r.filename, error: r.error, status: "error", items: [] };
      return {
        filename: r.filename,
        thumbnail_b64: r.thumbnail_b64 || "",
        drink_name: r.data.drink_name || "",
        cup_size_oz: r.data.cup_size_oz || "",
        full_name: r.data.full_name || "",
        match_product_id: r.data.match_product_id || "",
        match_product_name: r.data.match_product_name || "",
        items: (r.data.items || []).map((it) => ({
          extracted_name: it.extracted_name,
          qty: it.qty,
          unit: it.unit,
          unit_original: it.unit_original || "",
          match_ingredient_id: it.match_ingredient_id || "",
          match_name: it.match_name || "",
          match_unit: it.match_unit || "",
          accepted: !!it.accepted,
        })),
        status: "pending", // pending | committed | duplicate | error
        message: "",
      };
    });
    setParsedFiles(cards);
    setPhase("review");
  };

  const patchCard = (filename, patch) =>
    setParsedFiles((s) => s.map((c) => (c.filename === filename ? { ...c, ...patch } : c)));
  const patchCardItem = (filename, idx, patch) =>
    setParsedFiles((s) => s.map((c) => c.filename !== filename ? c : {
      ...c,
      items: c.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));

  // "sync" button next to a recipe unit — patches the ingredient's stored
  // unit to match what the recipe says (e.g. Milk stored as "pack" → "ml").
  // Also rewrites every recipe row referencing that ingredient in the
  // current session so the amber pill disappears immediately.
  const syncIngredientUnit = async (ingredientId, newUnit) => {
    try {
      const { data: updated } = await api.patch(`/mgmt/ingredients/${ingredientId}`, { unit: newUnit });
      setIngredientsCatalog((s) => s.map((ing) => (ing.id === ingredientId ? { ...ing, unit: updated.unit || newUnit } : ing)));
      setParsedFiles((s) => s.map((c) => ({
        ...c,
        items: c.items.map((it) => it.match_ingredient_id === ingredientId
          ? { ...it, match_unit: updated.unit || newUnit }
          : it),
      })));
    } catch (e) {
      window.alert(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const commit = async () => {
    setErr("");
    const targets = parsedFiles.filter((c) => !c.error && c.status !== "committed");
    if (!targets.length) {
      setErr("Nothing to import — everything is committed or failed.");
      return;
    }
    setPhase("committing");
    let ok = 0, fail = 0;
    for (const c of targets) {
      const items = c.items
        .filter((it) => it.accepted && it.match_ingredient_id && Number(it.qty) > 0)
        .map((it) => ({ ingredient_id: it.match_ingredient_id, qty: Number(it.qty) }));
      const productName = (c.full_name || c.drink_name || "").trim();
      // Allow committing even when zero ingredients mapped — the product still
      // gets created (e.g. ICE LATTE) so the admin can wire the recipe later.
      // Only block if we have neither a target product nor a name to create one.
      if (!c.match_product_id && !productName) {
        patchCard(c.filename, { status: "error", message: "Could not read the drink name. Type it into 'Full name' and save again." });
        fail += 1;
        continue;
      }
      try {
        await api.post("/mgmt/recipes/commit", {
          product_id: c.match_product_id || null,
          new_product_name: c.match_product_id ? "" : productName,
          new_product_category: "Drink - Matcha",
          items,
          thumbnail_b64: c.thumbnail_b64 || "",
        });
        patchCard(c.filename, { status: "committed", message: "" });
        ok += 1;
      } catch (e) {
        const msg = formatErr(e?.response?.data?.detail) || e.message;
        patchCard(c.filename, { status: "error", message: msg });
        fail += 1;
      }
    }
    if (fail === 0 && ok > 0) { onSaved(); return; }
    setPhase("review");
    setErr(`Saved ${ok} of ${targets.length} recipes. ${fail ? `${fail} failed — see per-file message.` : ""}`);
  };

  const pendingCount = parsedFiles.filter((c) => !c.error && c.status !== "committed").length;

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50" data-testid="recipe-builder-modal">
      <div className="bg-bone w-full max-w-6xl rounded-sm p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-display text-xl text-ink" style={{ letterSpacing: "0.05em" }}>Scan Recipes</h3>
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-1">
              Drop 30-40 recipe cards · we extract qty × ingredient and match to your catalog
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="recipe-close" className="text-ink-soft hover:text-ink">
            <X size={20} />
          </button>
        </div>

        {phase === "pick" && (
          <div>
            <label
              htmlFor="recipe-file-input"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer?.files); }}
              className={`block border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-colors ${dragOver ? "border-matcha-dark bg-sage/15" : "border-ink/25 hover:border-matcha-dark"}`}
              data-testid="recipe-dropzone"
            >
              <FileText size={32} className="mx-auto mb-3 text-ink-soft" />
              <p className="text-ink text-sm mb-1">
                {files.length ? `${files.length} recipe${files.length === 1 ? "" : "s"} ready · drop more or click to add` : "Drop your recipe cards or click to browse"}
              </p>
              <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em]">JPEG · PNG · WEBP · PDF · 12 MB each</p>
              <input id="recipe-file-input" data-testid="recipe-file-input" type="file" multiple
                accept="image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf"
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              />
            </label>
            {files.length > 0 && (
              <ul className="mt-4 space-y-1.5 text-sm">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-3 bg-bone border border-ink/10 rounded-sm px-3 py-2">
                    <span className="flex items-center gap-2 min-w-0">
                      <FileText size={14} className="text-matcha-dark shrink-0" />
                      <span className="text-ink truncate">{f.name}</span>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                    </span>
                    <button type="button" onClick={() => removeFile(i)} className="text-ink-soft hover:text-red-700"><X size={14} /></button>
                  </li>
                ))}
              </ul>
            )}
            {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm mt-4">{err}</p>}
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">Cancel</button>
              <button type="button" data-testid="recipe-parse-btn" disabled={!files.length} onClick={runOCR}
                className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50">
                <Upload size={14} /> Scan {files.length || ""} Recipe{files.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        )}

        {phase === "uploading" && (
          <div className="py-16 text-center">
            <Loader2 size={36} className="mx-auto mb-4 text-matcha-dark animate-spin" />
            <p className="text-ink text-sm">Reading {files.length} recipe card{files.length === 1 ? "" : "s"} with AI…</p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-2">This usually takes 10–20 seconds per card</p>
          </div>
        )}

        {phase === "review" && parsedFiles.length > 0 && (
          <div className="space-y-4">
            {parsedFiles.map((c) => (
              <RecipeCard
                key={c.filename}
                card={c}
                ingredientsCatalog={ingredientsCatalog}
                productsCatalog={productsCatalog}
                onCardChange={(patch) => patchCard(c.filename, patch)}
                onItemChange={(idx, patch) => patchCardItem(c.filename, idx, patch)}
                onCatalogAdd={(ing) => setIngredientsCatalog((s) => [...s, ing])}
                onSyncIngredientUnit={syncIngredientUnit}
              />
            ))}
            {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm">{err}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => { setPhase("pick"); setParsedFiles([]); setFiles([]); }}
                className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink">← Rescan</button>
              <button type="button" data-testid="recipe-commit-btn" disabled={pendingCount === 0} onClick={commit}
                className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50">
                <CheckIcon size={14} /> Save {pendingCount || ""} recipe{pendingCount === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        )}

        {phase === "committing" && (
          <div className="py-16 text-center">
            <Loader2 size={36} className="mx-auto mb-4 text-matcha-dark animate-spin" />
            <p className="text-ink text-sm">Saving recipes…</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Small, reusable ingredient-picker cell used inside both the Receipt scanner
// and the Recipe Builder. Renders the standard `<select>` sourced from the
// catalog + an inline "+ New" affordance that pops up a 3-field mini-form
// (name / unit / cost) so the admin can create a missing ingredient without
// leaving the modal. Newly-created ingredients are bubbled up to the parent
// via `onCatalogAdd` so every other row in the same session picks them up.
function IngredientPickerCell({
  value,
  onChange,           // (patch) => void  — patch includes match_ingredient_id, match_name, match_unit, accepted
  ingredientsCatalog,
  onCatalogAdd,       // (newIngredient) => void
  extractedName = "", // prefill for the "+ New" name field
  unitHint = "",      // prefill for the "+ New" unit field
  testidPrefix = "ing-picker",
}) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", unit: "ml", cost_per_unit: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const start = () => {
    setCreating(true);
    setErr("");
    setForm({
      name: extractedName || "",
      unit: unitHint || "ml",
      cost_per_unit: "",
    });
  };
  const cancel = () => { setCreating(false); setErr(""); };
  const save = async () => {
    setErr("");
    const name = (form.name || "").trim();
    if (!name) { setErr("Name is required."); return; }
    setSaving(true);
    try {
      const { data: ing } = await api.post("/mgmt/ingredients", {
        name,
        unit: form.unit,
        cost_per_unit: Number(form.cost_per_unit || 0),
        kind: "physical",
      });
      onCatalogAdd(ing);
      onChange({
        match_ingredient_id: ing.id,
        match_name: ing.name,
        match_unit: ing.unit,
        accepted: true,
      });
      setCreating(false);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (creating) {
    return (
      <div className="space-y-2 p-2 rounded-sm border border-matcha-dark/40 bg-sage/15" data-testid={`${testidPrefix}-form`}>
        <div className="grid grid-cols-3 gap-2">
          <input
            className={inputCls + " col-span-1"}
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            data-testid={`${testidPrefix}-name`}
          />
          <select
            className={inputCls}
            value={form.unit}
            onChange={(e) => setForm((s) => ({ ...s, unit: e.target.value }))}
            data-testid={`${testidPrefix}-unit`}
          >
            {["ml", "g", "piece", "pack", "kg", "l", "can", "bottle", "box"].map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <input
            className={inputCls + " text-right"}
            type="number" step="0.01" min="0"
            placeholder={`RM per ${form.unit}`}
            value={form.cost_per_unit}
            onChange={(e) => setForm((s) => ({ ...s, cost_per_unit: e.target.value }))}
            data-testid={`${testidPrefix}-cost`}
          />
        </div>
        {err && <p className="text-[11px] text-red-700">{err}</p>}
        <div className="flex items-center gap-2">
          <button type="button" onClick={save} disabled={saving} data-testid={`${testidPrefix}-save`}
            className="text-[11px] uppercase tracking-[0.22em] px-3 py-1 rounded-full bg-matcha-dark text-bone hover:bg-ink transition-colors disabled:opacity-50">
            {saving ? "Saving…" : "+ Add"}
          </button>
          <button type="button" onClick={cancel} className="text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-ink">Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <select
        className={inputCls + " flex-1 min-w-0"}
        value={value || ""}
        onChange={(e) => {
          const id = e.target.value;
          const ing = ingredientsCatalog.find((x) => x.id === id);
          // When the admin clears the map back to the placeholder we treat
          // it as "auto-create on save" (not un-ticking the row) as long
          // as the OCR gave us an extracted name to build from.
          onChange({
            match_ingredient_id: id,
            match_name: ing?.name || "",
            match_unit: ing?.unit || "",
            accepted: !!id || !!(extractedName || "").trim(),
          });
        }}
        data-testid={`${testidPrefix}-select`}
      >
        {/* Placeholder tells the admin exactly what "unmapped" means: if
            they leave this alone, the row will auto-create a fresh
            ingredient with the OCR name on Save. Only when the OCR string
            is empty does this fall back to a neutral "Skip" label. */}
        <option value="">
          {extractedName ? `+ Create new: ${extractedName}` : "— Skip —"}
        </option>
        {ingredientsCatalog.map((ing) => (
          <option key={ing.id} value={ing.id}>
            {ing.name} {ing.code ? `(${ing.code})` : ""} · per {ing.unit || "?"}
          </option>
        ))}
      </select>
      {!value && (
        <button type="button" onClick={start}
          className="whitespace-nowrap text-[11px] uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-full border border-matcha-dark text-matcha-dark hover:bg-matcha-dark hover:text-bone transition-colors"
          data-testid={`${testidPrefix}-new-btn`}
          title={extractedName ? `Create "${extractedName}" as a new ingredient` : "Create a new ingredient"}>
          + New
        </button>
      )}
    </div>
  );
}

// Single recipe card in the review phase. Renders the extracted product name +
// cup size + editable ingredient rows with catalog dropdowns. Every field is
// editable — admin can override anything the AI got wrong before commit.
// If an OCR line has no auto-match, the row exposes a "+ New" inline form
// (via IngredientPickerCell) that POSTs to /mgmt/ingredients and auto-selects
// the new item.
function RecipeCard({ card, ingredientsCatalog, productsCatalog, onCardChange, onItemChange, onCatalogAdd, onSyncIngredientUnit }) {
  const statusColour =
    card.error ? "border-red-300 bg-red-50" :
    card.status === "committed" ? "border-matcha-dark/40 bg-sage/15" :
    card.status === "error" ? "border-red-300 bg-red-50" :
    "border-ink/10 bg-bone";
  return (
    <div className={`rounded-sm border p-4 ${statusColour}`} data-testid={`recipe-card-${card.filename}`}>
      <div className="flex items-baseline gap-2 flex-wrap mb-3">
        <FileText size={14} className="text-matcha-dark" />
        <span className="text-ink text-sm font-medium truncate flex-1">{card.filename}</span>
        {card.status === "committed" && (
          <span className="text-[11px] uppercase tracking-[0.22em] text-matcha-dark">✓ Saved</span>
        )}
        {card.status === "error" && !card.error && (
          <span className="text-[11px] uppercase tracking-[0.22em] text-red-700">Failed</span>
        )}
      </div>
      {card.error && <p className="text-[12px] text-red-700 mb-2">{card.error}</p>}
      {card.message && <p className="text-[12px] text-red-700 mb-2">{card.message}</p>}
      {!card.error && (
        <div className="flex gap-4 flex-col sm:flex-row">
          {card.thumbnail_b64 && (
            <img
              src={card.thumbnail_b64}
              alt={card.filename}
              className="w-24 h-24 rounded-sm object-cover border border-ink/10 shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="sm:col-span-2">
              <label className="overline text-ink-soft block mb-1">
                Attach to product
                {!card.match_product_id && (
                  <span className="ml-2 text-matcha-dark normal-case tracking-normal text-[10px]">
                    · No match found — new product will be created on save
                  </span>
                )}
              </label>
              <select
                data-testid={`recipe-product-${card.filename}`}
                className={inputCls + " w-full"}
                value={card.match_product_id}
                onChange={(e) => onCardChange({ match_product_id: e.target.value })}
              >
                <option value="">— Create new: {card.full_name || card.drink_name} —</option>
                {productsCatalog.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <p className="text-[10px] text-ink-soft uppercase tracking-[0.18em] mt-1">
                Extracted: <span className="text-ink normal-case tracking-normal">{card.drink_name || "—"}</span>
                {card.cup_size_oz ? <span className="ml-1 text-matcha-dark">· {card.cup_size_oz}oz</span> : null}
              </p>
            </div>
            <div>
              <label className="overline text-ink-soft block mb-1">Full name (if new)</label>
              <input
                className={inputCls + " w-full"}
                value={card.full_name}
                onChange={(e) => onCardChange({ full_name: e.target.value })}
                disabled={!!card.match_product_id}
              />
            </div>
          </div>

          <div className="border border-ink/10 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-sage/40 text-left text-ink-soft">
                <tr>
                  <th className="px-3 py-2 overline w-8"></th>
                  <th className="px-3 py-2 overline">On the card</th>
                  <th className="px-3 py-2 overline">Map to ingredient</th>
                  <th className="px-3 py-2 overline text-right">Qty</th>
                  <th className="px-3 py-2 overline">Unit</th>
                </tr>
              </thead>
              <tbody>
                {card.items.map((it, i) => (
                  <tr key={i} className={`border-t border-ink/5 ${it.accepted ? "bg-sage/10" : ""}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" className="w-4 h-4 accent-matcha-dark"
                        checked={it.accepted}
                        onChange={() => onItemChange(i, { accepted: !it.accepted })}
                      />
                    </td>
                    <td className="px-3 py-2 text-ink">{it.extracted_name || "—"}</td>
                    <td className="px-3 py-2">
                      <IngredientPickerCell
                        value={it.match_ingredient_id}
                        onChange={(patch) => onItemChange(i, patch)}
                        ingredientsCatalog={ingredientsCatalog}
                        onCatalogAdd={onCatalogAdd}
                        extractedName={it.extracted_name}
                        unitHint={it.unit || it.match_unit || "ml"}
                        testidPrefix={`recipe-ing-${i}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" step="0.001" min="0"
                        className={inputCls + " w-20 text-right"}
                        value={it.qty}
                        onChange={(e) => onItemChange(i, { qty: e.target.value })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <select
                          data-testid={`recipe-unit-${i}`}
                          className={inputCls + " w-20 text-[11px] uppercase tracking-[0.12em]"}
                          value={it.unit || it.match_unit || "ml"}
                          onChange={(e) => onItemChange(i, { unit: e.target.value })}
                        >
                          {["ml", "g", "piece", "pack", "kg", "l", "litre", "can", "bottle", "box"].map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                        {/* Warn + one-click "sync" when the recipe unit differs from
                            the mapped ingredient's stored unit. Prevents cost math
                            mistakes like "140 ml × RM/pack". */}
                        {it.match_ingredient_id && it.match_unit && it.unit && it.match_unit !== it.unit && (
                          <button
                            type="button"
                            data-testid={`recipe-unit-sync-${i}`}
                            onClick={() => onSyncIngredientUnit(it.match_ingredient_id, it.unit)}
                            className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-600 hover:text-bone transition-colors whitespace-nowrap"
                            title={`Stored as "${it.match_unit}" — click to update the ingredient to "${it.unit}"`}
                          >
                            sync
                          </button>
                        )}
                      </div>
                      {it.unit_original && (
                        <p className="text-[9px] text-amber-700 mt-1 normal-case tracking-normal"
                          title={`OCR read "${it.unit_original}" — normalized to ml for liquids`}>
                          was {it.unit_original}
                        </p>
                      )}
                    </td>
                  </tr>
                ))}
                {card.items.length === 0 && (
                  <tr><td colSpan={5} className="p-4 text-center text-ink-soft">No ingredients detected.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

